import { Component, OnInit, OnDestroy, AfterViewInit, HostListener, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { of, Subject, combineLatest, forkJoin } from 'rxjs';
import { catchError, takeUntil, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { HttpContext } from '@angular/common/http';
import { SKIP_ERROR_NOTIFICATION } from '../../../core/error-handling/services/http-error-notification.interceptor';
import { AvaliacaoService, ConfiguracaoAvaliacao, FormulaCalculo, CalculoFormula, ItemAvaliavel, RegistroAvaliacao } from '../../../core/config/services/avaliacao.service';
import { NotaService } from '../services/nota.service';
import { LinhaDesempenho, ColunaDesempenho, NotaView, ItemEducacional, Periodo, Encerramento, EnturmacaoCompleta } from '../models/nota-types';
import {
  clearFields,
  convertNotaValue,
  deveExibirParecer,
  formatPeriodoDescricao,
  getDataPublicacaoAvaliacao,
  getMensagemSucessoPublicacaoAvaliacao,
  getRotuloAvaliacaoPublicacao,
  getSituacaoDescricaoCurta,
  isAvaliacaoPublicada,
  isNotaComConteudoPublicavel,
  isNotaMediaPeriodo,
  isPeriodoLetivoEncerrado,
  limitText,
  matchesEncerramento,
  resolveModoAvaliacaoNota,
  resolveNomeAluno,
  resolveNotaParaSalvar,
  resolveNumeroChamada,
  selectPeriodoAutomatico,
  verificaDataFimEnturmacao
} from '../utils/desempenho-escolar-nota.utils';
import { ContextService } from '../../../core/context/services/context.service';
import { MobileContextService, ContextoPWA } from '../../../core/context/services/mobile-context.service';
import { ConfigService } from '../../../core/config/services/config.service';
import { ErrorHandlerService } from '../../../core/error-handling/services/error-handler.service';
import { DesempenhoFiltersService } from '../services/desempenho-filters.service';
import { DesempenhoConfigService } from '../services/desempenho-config.service';
import { DesempenhoDataService, Turma } from '../services/desempenho-data.service';
import {
  InstrumentoArquivoAnexo,
  InstrumentoAvaliacaoRegistro,
  InstrumentoAvaliacaoService,
  InstrumentoConfiguracaoEstabelecimento,
  InstrumentoItemEducacionalOption,
  InstrumentoPeriodoOption
} from '../services/instrumento-avaliacao.service';
import { NotificationService } from '@app/core/services/notification.service';
import { CalendarioEstabelecimento, CalendarioService } from '../../frequencia/services/calendario.service';
import { ParecerService, Parecer } from '../../parecer/services/parecer.service';
import { InstrumentosAvaliacaoFacadeService } from '@features/instrumentos-avaliacao/services/instrumentos-avaliacao-facade.service';
import { APP_CONFIG, STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '../../../shared/constants';
import { PrototypeSelectOption } from '@infrastructure/ui/components/forms/prototype-select/prototype-select.component';
import { PeriodoSelectOption } from '@infrastructure/ui/components/forms/periodo-select/periodo-select.component';
import { AnexoItem } from '@infrastructure/ui/components/anexos/anexos.component';
import {
  INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS,
  INSTRUMENTO_ANEXO_TAMANHO_MAXIMO,
  InstrumentoArquivoState,
  InstrumentoCampoAdicionalConfig,
  InstrumentoFormularioState,
  InstrumentoResumoView,
  InstrumentoTipoOption,
  InstrumentoTurmaOption,
  TipoInstrumentoAvaliacaoId
} from '../models/instrumento-avaliacao.types';
import { InstrumentoItemEducacional } from '@features/instrumentos-avaliacao/models/instrumento-avaliacao.types';
import { InstrumentoFormularioHost } from '@features/instrumentos-avaliacao/models/instrumento-formulario-host.interface';
import { InstrumentoAvaliacaoHostBase } from '@features/instrumentos-avaliacao/models/instrumento-avaliacao-host.base';
import {
  buildInstrumentoAnexosAccept,
  buildInstrumentoAnexosLabel,
  cloneInstrumentoCampoAdicional,
  construirInstrumentoPayload,
  createInstrumentoFormulario,
  extractTurmaIdFromRaw,
  formatarDataHoraPublicacao,
  formatResultFormulaCalculo,
  getHojeISO,
  getTipoInstrumentoLabel,
  mapInstrumentoArquivo,
  mapInstrumentoArquivos,
  mapInstrumentoToForm,
  mergeInstrumentoComResumo,
  parseDate,
  parseOptionalNumber,
  parseUnknownNumber,
  resolveInstrumentoArquivoIdentificador,
  sanitizeInstrumentoCampoNumerico,
  serializeInstrumentoArquivos,
  serializeInstrumentoCampoAdicional,
  serializeInstrumentoForm,
  toInstrumentoRegistro,
  toNullableNumber,
  validarInstrumentoFormulario
} from '../utils/instrumento-avaliacao.utils';
import { InstrumentoAvaliacaoModalComponent } from '@features/instrumentos-avaliacao/components/instrumento-avaliacao-modal.component';

interface Enturmacao {
  id: number;
  matricula: {
    id: number;
    nome: string;
    nomeSocial?: string;
    situacao: string;
    avaliacaoDescritiva?: boolean;
  };
  numeroChamada: number;
  situacao: string;
  dataFim?: string;
  risco?: {
    classificacaoEvasao: string;
    classificacaoReprovacao: string;
  };
}

type NotaViewCompat = NotaView & {
  $$mensagemValidacao?: string | null;
  parecer?: string;
  parecerDetalhadoConteudo?: string;
  enturmacao?: {
    matricula?: {
      nome?: string;
      nomeSocial?: string;
    };
  };
};

type OrdenacaoAlunos = 'nomeAsc' | 'nomeDesc' | 'numeroChamadaAsc' | 'numeroChamadaDesc';

@Component({
  selector: 'app-desempenho-escolar',
  templateUrl: './desempenho-escolar.component.html',
  styleUrls: ['./desempenho-escolar.component.scss']
})
/**
 * Página de desempenho escolar do PWA.
 * Reúne o contexto acadêmico atual, a grade de avaliação e o fluxo de instrumentos.
 */
export class DesempenhoEscolarComponent extends InstrumentoAvaliacaoHostBase implements OnInit, OnDestroy, AfterViewInit, InstrumentoFormularioHost {
    /**
     * Cor da disciplina pelo índice visual, igual ao modal/contexto.
     */
    getDisciplinaCorPorIndex(index: number): string {
      return this.mobileContextService.colorService.getColorByIndex(index);
    }
  /**
   * Sempre sincroniza as cores dos componentes curriculares com o contexto, garantindo cor fixa e correta.
   */
  private syncCoresComponentes(): void {
    if (Array.isArray(this.itensEducacionais) && this.itensEducacionais.length > 0) {
      this.mobileContextService.colorService.setContextComponentes(this.itensEducacionais);
    }
  }

  /**
   * Resolve a cor da disciplina para o dot, usando o serviço de cor por ID igual ao contexto/modal.
   */
  getDisciplinaCorPorId(id?: number): string {
    if (!id) return '#A259F7';
    return this.mobileContextService.colorService.getColor(id) || '#A259F7';
  }
  readonly vm = this;

  @ViewChild('instrumentoModalRef') instrumentoModalRef?: InstrumentoAvaliacaoModalComponent;

  protected destroy$ = new Subject<void>();
  private closeInstrumentoFormulario$ = new Subject<void>();

  turma: Turma | null = null;
  periodo: Periodo | null = null;
  periodos: Periodo[] = [];
  itemEducacional: ItemEducacional | null = null;
  itensEducacionais: ItemEducacional[] = [];

  configuracaoAvaliacao: ConfiguracaoAvaliacao = {};
  configuracaoCha: Record<string, unknown> = {};

  colunas: ColunaDesempenho[] = [];
  linhas: LinhaDesempenho[] = [];
  colunaMediaPeriodo: ColunaDesempenho | null = null;

  porInstrumento = true;
  semCalendario = false;
  periodosPronto = false;
  loadingTracker = false;
  saveTracker = false;
  calculoTracker = false;
  loadOnEnd = false;
  ocultarDetalhes = false;
  ocultarCardControles = false;
  successfulSave = false;
  itensEducacionaisTotal: ItemEducacional[] = [];
  private isLoadingRegistros = false; // Flag para evitar múltiplas chamadas simultâneas

  showErrorModal = false;
  errorMessage = '';
  parecerModalAberto = false;
  parecerModalCarregando = false;
  parecerModalSalvando = false;
  parecerModalTexto = '';
  parecerModalNota: NotaView | null = null;
  parecerModalEhEdicao = false;
  avaliacaoConfirmacaoAberta = false;
  avaliacaoConfirmacaoNota: NotaView | null = null;
  avaliacaoConfirmacaoPublicar = false;
  avaliacaoConfirmacaoTitulo = '';
  avaliacaoPublicandoId: number | null = null;
  avaliacaoDescritivaModalAberto = false;
  avaliacaoDescritivaModalTexto = '';
  avaliacaoDescritivaModalNota: NotaView | null = null;

  publicacaoAnualModalAberta = false;

  dataExamesModalAberta = false;

  searchControl = new FormControl('');
  showSearchDropdown = false;
  showOrdenacaoDropdown = false;
  searchInputFocused = false;
  selectedAluno: LinhaDesempenho | null = null;
  searchFilter = { keywords: '' };
  ordenacaoAtual: OrdenacaoAlunos = 'numeroChamadaAsc';

  filtroSituacaoEnturmacao: string | null = null;
  readonly opcoesSituacaoEnturmacao: { valor: string; label: string }[] = [
    { valor: 'MATRICULADO',             label: 'Matriculado' },
    { valor: 'CURSANDO',                label: 'Cursando' },
    { valor: 'TRANSFERIDO',             label: 'Transferido' },
    { valor: 'DEIXOU_FREQUENTAR',       label: 'Deixou de frequentar' },
    { valor: 'FALECIDO',                label: 'Falecido' },
    { valor: 'CANCELAMENTO',            label: 'Cancelamento' },
    { valor: 'EM_EXAME',                label: 'Em exame' },
    { valor: 'APROVADO',                label: 'Aprovado' },
    { valor: 'APROVADO_DEPENDENCIA',    label: 'Aprovado(a) parcialmente' },
    { valor: 'REPROVADO',               label: 'Reprovado' },
    { valor: 'REPROVADO_FREQUENCIA',    label: 'Reprovado por frequência' },
    { valor: 'RECLASSIFICADO_AVANCO',   label: 'Reclassificado(a) - Avanço' },
    { valor: 'RECLASSIFICADO_ACELERACAO', label: 'Reclassificado(a) - Aceleração' },
    { valor: 'EM_ANDAMENTO',            label: 'Em andamento' },
    { valor: 'DISPENSADO',              label: 'Dispensado' },
    { valor: 'APROVADO_CONSELHO',       label: 'Aprovado(a) pelo conselho' },
    { valor: 'REMANEJADO',              label: 'Remanejado(a) internamente' },
  ];
  get instrumentoVisualizadoId(): number | null { return this.instrumentosFacade.instrumentoVisualizadoId; }
  set instrumentoVisualizadoId(value: number | null) { this.instrumentosFacade.instrumentoVisualizadoId = value; }

  formulaCalculo: FormulaCalculo | null = null;
  calculoFormula: CalculoFormula | null = null;
  formulasCalculo: FormulaCalculo[] = [];

  disabledPrev = true;
  disabledNext = false;

  showVisualizarDropdown = false;
  showDisciplinaDropdown = false;
  showInstrumentoDropdown = false;
  showPeriodoDropdown = false;
  showFormulaDropdown = false;

  URL_FAQ_MACHINE_LEARNING = '/faq/machine-learning';
  MIN_SCREEN_WIDTH = 768;
  RECORD_TYPE_DESEMPENHO = 'com.betha.educacao.integracao.event.RegistroAvaliacaoIntegration';

  enumSituacaoMatricula: Record<string, { description?: string; value?: unknown }> = {};
  enumSituacaoDisciplina: Record<string, { description?: string; value?: unknown }> = {};
  grupoEnturmacaoCor: Record<string, { description?: string; value?: unknown }> = {};

  // Instrumentos de avaliação: getters/setters de delegação, snapshots e
  // helpers idênticos entre as telas vivem em InstrumentoAvaliacaoHostBase.

  constructor(
    private avaliacaoService: AvaliacaoService,
    private notaService: NotaService,
    private contextService: ContextService,
    protected mobileContextService: MobileContextService,
    private router: Router,
    private configService: ConfigService,
    private errorHandler: ErrorHandlerService,
    private desempenhoFilters: DesempenhoFiltersService,
    private desempenhoConfig: DesempenhoConfigService,
    private desempenhoData: DesempenhoDataService,
    protected instrumentoAvaliacaoService: InstrumentoAvaliacaoService,
    instrumentosFacade: InstrumentosAvaliacaoFacadeService,
    protected notificationService: NotificationService,
    protected calendarioService: CalendarioService,
    private parecerService: ParecerService
  ) {
    super(instrumentosFacade);
  }

  /**
   * Retorna o índice da disciplina atualmente selecionada.
   */
  getDisciplinaIndexSelecionada(): number {
    if (!this.itemEducacional || !this.itensEducacionais) return -1;
    return this.itensEducacionais.findIndex((e: ItemEducacional) => e.id === this.itemEducacional!.id);
  }

  /**
   * Inicializa a tela com o contexto atual e reage a trocas de turma.
   */
  async ngOnInit(): Promise<void> {
    // O contexto já foi carregado pelo AppComponent via MobileContextService
    await this.initializeComponent();
    if (this.isStandaloneInstrumentosPage) {
      this.openPorInstrumento();
    }
    this.setupSearchSubscription();
    
    // Observa mudanças no contexto e reinicializa quando mudar
    let turmaIdAnterior = this.turma?.id;
    this.mobileContextService.contexto$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(async contexto => {
      if (contexto && contexto.turma && contexto.turma.id) {
        const turmaIdAtual = contexto.turma.id;
        if (turmaIdAnterior !== turmaIdAtual) {
          turmaIdAnterior = turmaIdAtual;
          await this.initializeComponent();
        }
      } else if (!contexto || !contexto.turma) {
        turmaIdAnterior = undefined;
      }
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.instrumentosFacade.reset();
  }

  ngAfterViewInit(): void {
    // Fecha dropdowns ao clicar fora
    this.setupDropdownCloseListeners();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedButton = target.closest('button[type="button"]');
    const clickedDropdown = target.closest('ul');
    const clickedDropdownRoot = target.closest('[data-dropdown-root]');
    const clickedButtonId = clickedButton?.id?.toLowerCase() || '';
    const clickedButtonText = clickedButton?.textContent?.toLowerCase() || '';

    if (!clickedButtonId.includes('disciplina') &&
      !clickedButtonId.includes('instrumento') &&
        !clickedButtonId.includes('periodo') &&
        !clickedButtonId.includes('formula') &&
        !clickedButtonId.includes('ordenacao') &&
        !clickedButtonId.includes('visualizar') &&
        !clickedButtonText.includes('visualizar') &&
        !clickedButtonText.includes('visualizando por') &&
        !clickedDropdownRoot &&
        !clickedDropdown) {
      this.showVisualizarDropdown = false;
      this.showDisciplinaDropdown = false;
      this.showInstrumentoDropdown = false;
      this.showPeriodoDropdown = false;
      this.showFormulaDropdown = false;
      this.showOrdenacaoDropdown = false;
    }
  }

  private setupDropdownCloseListeners(): void {
    // stub — implementação adicional se necessário
  }

  toggleVisualizarDropdown(): void {
    this.showVisualizarDropdown = !this.showVisualizarDropdown;
    this.showDisciplinaDropdown = false;
    this.showInstrumentoDropdown = false;
    this.showPeriodoDropdown = false;
    this.showFormulaDropdown = false;
    this.showOrdenacaoDropdown = false;
  }

  toggleDisciplinaDropdown(): void {
    this.showDisciplinaDropdown = !this.showDisciplinaDropdown;
    this.showVisualizarDropdown = false;
    this.showInstrumentoDropdown = false;
    this.showPeriodoDropdown = false;
    this.showFormulaDropdown = false;
    this.showOrdenacaoDropdown = false;
  }

  toggleInstrumentoDropdown(): void {
    this.showInstrumentoDropdown = !this.showInstrumentoDropdown;
    this.showVisualizarDropdown = false;
    this.showDisciplinaDropdown = false;
    this.showPeriodoDropdown = false;
    this.showFormulaDropdown = false;
    this.showOrdenacaoDropdown = false;
  }

  closeDropdowns(): void {
    this.showVisualizarDropdown = false;
    this.showDisciplinaDropdown = false;
    this.showInstrumentoDropdown = false;
    this.showPeriodoDropdown = false;
    this.showFormulaDropdown = false;
    this.showOrdenacaoDropdown = false;
  }

  togglePeriodoDropdown(): void {
    this.showPeriodoDropdown = !this.showPeriodoDropdown;
    this.showVisualizarDropdown = false;
    this.showDisciplinaDropdown = false;
    this.showInstrumentoDropdown = false;
    this.showFormulaDropdown = false;
    this.showOrdenacaoDropdown = false;
  }

  toggleFormulaDropdown(): void {
    this.showFormulaDropdown = !this.showFormulaDropdown;
    this.showVisualizarDropdown = false;
    this.showDisciplinaDropdown = false;
    this.showPeriodoDropdown = false;
    this.showInstrumentoDropdown = false;
    this.showOrdenacaoDropdown = false;
  }

  toggleOrdenacaoDropdown(): void {
    this.showOrdenacaoDropdown = !this.showOrdenacaoDropdown;
    this.showVisualizarDropdown = false;
    this.showDisciplinaDropdown = false;
    this.showPeriodoDropdown = false;
    this.showInstrumentoDropdown = false;
  }

  setOrdenacao(ordem: OrdenacaoAlunos): void {
    this.ordenacaoAtual = ordem;
    this.showOrdenacaoDropdown = false;
  }

  ciclarOrdenacaoCompacta(): void {
    switch (this.ordenacaoAtual) {
      case 'numeroChamadaAsc':
        this.ordenacaoAtual = 'numeroChamadaDesc';
        break;
      case 'numeroChamadaDesc':
        this.ordenacaoAtual = 'nomeAsc';
        break;
      case 'nomeAsc':
        this.ordenacaoAtual = 'nomeDesc';
        break;
      case 'nomeDesc':
      default:
        this.ordenacaoAtual = 'numeroChamadaAsc';
        break;
    }
  }

  changePeriodo(periodo: Periodo): void {
    if (!periodo || !this.turma) return;

    if (this.periodo?.id === periodo.id) {
      this.closeDropdowns();
      return;
    }

    this.periodo = periodo;
    this.closeDropdowns();

    this.colunas = [];
    this.linhas = [];
    this.colunaMediaPeriodo = null;
    this.calculoFormula = null;
    this.formulaCalculo = null;
    this.hasAvaliacao = false;
    this.instrumentoVisualizadoId = null;

    if (!this.itemEducacional) {
      this.loadInstrumentosTelaAtual().catch(() => undefined);
      return;
    }

    this.atualizaListaAvaliacao();
  }

  onPeriodoOptionSelected(option: PeriodoSelectOption): void {
    const periodoSelecionado = this.periodos.find((item) => item.id === option.id);
    if (periodoSelecionado) {
      this.changePeriodo(periodoSelecionado);
      return;
    }

    this.periodo = {
      id: Number.isNaN(Number(option.id)) ? option.id : Number(option.id),
      descricao: option.descricao,
      dataInicial: option.dataInicial || '',
      dataFinal: option.dataFinal || ''
    } as Periodo;
  }

  formatPeriodoDescricao(periodo: Periodo): string {
    return formatPeriodoDescricao(periodo);
  }

  limitText(value: string | null | undefined, maxLength = 50): string {
    return limitText(value, maxLength);
  }

  /**
   * Extrai mensagem legível de um JSON ou objeto.
   * Exemplo: {"RegistroAvaliacao":"Existem encerramentos..."} -> "Existem encerramentos..."
   */
  private extractReadableMessage(message: string): string {
    if (!message) return 'Erro desconhecido';

    return this.errorHandler.extractErrorMessageComplete({ error: { message }, message });
  }

  closeErrorModal(): void {
    this.showErrorModal = false;
    this.errorMessage = '';
  }

  get parecerModalTitulo(): string {
    return this.parecerModalEhEdicao ? 'Editando parecer' : 'Adicionando parecer';
  }

  get parecerModalNomeAluno(): string {
    const matricula = (this.parecerModalNota as NotaViewCompat | null)?.enturmacao?.matricula;
    return String(matricula?.nomeSocial || matricula?.nome || 'Aluno(a)');
  }

  get avaliacaoDescritivaModalNomeAluno(): string {
    const matricula = (this.avaliacaoDescritivaModalNota as NotaViewCompat | null)?.enturmacao?.matricula;
    return String(matricula?.nomeSocial || matricula?.nome || 'Aluno(a)');
  }

  get avaliacaoDescritivaModalTitulo(): string {
    const descricao = String(this.instrumentoVisualizadoResumo?.descricao || this.periodo?.descricao || 'Avaliação descritiva').trim();
    return descricao || 'Avaliação descritiva';
  }

  get avaliacaoDescritivaModalReadonly(): boolean {
    const nota = this.avaliacaoDescritivaModalNota;
    if (!nota) {
      return true;
    }

    const editavel = typeof nota.$$editavelAnual === 'boolean' ? nota.$$editavelAnual : nota.$$editavel;
    return !editavel || !!nota.$$isSaving;
  }

  /**
   * Verifica se o período avaliativo está encerrado
   * Baseado na lógica do AngularJS desempenho-periodo-view.service.js
   */
  isPeriodoEncerrado(periodo: Periodo): boolean {
    if (!this.hasValidPeriodoAndEncerramentos(periodo)) {
      return false;
    }

    const encerramento = this.findEncerramentoForPeriodo(periodo);
    return !!encerramento;
  }

  private hasValidPeriodoAndEncerramentos(periodo: Periodo | null): boolean {
    return !!(periodo && this.encerramentosTurma && this.encerramentosTurma.length > 0);
  }

  private findEncerramentoForPeriodo(periodo: Periodo): unknown {
    return this.encerramentosTurma.find(enc => this.matchesEncerramento(enc, periodo));
  }

  private matchesEncerramento(
    enc: { tipoPeriodo?: string; identificadorExameFinal?: string; status?: string; periodoId?: number },
    periodo: Periodo
  ): boolean {
    return matchesEncerramento(enc, periodo);
  }

  private loadPeriodos(): void {
    if (!this.turma || !this.turma.id) {
      return;
    }

    this.semCalendario = false;
    this.periodosPronto = false;
    this.avaliacaoService.getPeriodosAvaliativos(this.turma.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (periodos) => {
          this.periodos = periodos || [];
          if (this.periodos.length === 0 && (this.isTurmaAtividade || this.isTurmaEjaModular)) {
            // Turma de atividade / EJA modular: não tem períodos avaliativos convencionais.
            // Cria período sintético com dataInicio/dataFim do itemEducacional.
            this.carregarPeriodoSinteticoAtividade();
          } else {
            this.periodosPronto = true;
            this.semCalendario = this.periodos.length === 0;
            this.finalizarLoadPeriodos();
          }
        },
        error: (error) => {
          this.periodosPronto = true;
          if (error.status === 404) {
            this.periodos = [];
            if (this.isTurmaAtividade || this.isTurmaEjaModular) {
              this.carregarPeriodoSinteticoAtividade();
              return;
            }
            this.semCalendario = true;
          }
          this.loadInstrumentosTelaAtual().catch(() => undefined);
          // Continua mesmo com erro - pode ser que a turma não tenha períodos configurados
        }
      });
  }

  /**
   * Para turmas de atividade/EJA modular, cria um período sintético a partir das
   * datas do itemEducacional (dataInicio/dataFim), seguindo o mesmo padrão da
   * tela de Frequência e Acompanhamento Pedagógico.
   */
  private carregarPeriodoSinteticoAtividade(): void {
    // itemEducacional pode ainda não estar definido quando loadPeriodos termina
    // (carregam em paralelo). Usa o primeiro da lista de contexto como fallback.
    const itemId = this.itemEducacional?.id || this.itensEducacionais[0]?.id;

    if (!itemId) {
      // Sem componente no contexto — não marca semCalendario, é atividade
      this.periodosPronto = true;
      this.semCalendario = false;
      this.finalizarLoadPeriodos();
      return;
    }

    this.instrumentoAvaliacaoService.getItemEducacional(itemId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (item) => {
          if (item?.dataInicio && item?.dataFim) {
            this.periodos = [{
              id: 0,
              descricao: 'Período da atividade',
              dataInicial: item.dataInicio,
              dataFinal: item.dataFim
            }];
          }
          // Para turma de atividade/modular nunca exibe "sem calendário"
          this.semCalendario = false;
          this.periodosPronto = true;
          this.finalizarLoadPeriodos();
        },
        error: () => {
          // Mesmo com erro na busca do itemEducacional, não bloqueia a tela
          this.semCalendario = false;
          this.periodosPronto = true;
          this.finalizarLoadPeriodos();
        }
      });
  }

  /**
   * Finaliza o fluxo de carregamento de períodos: seleciona período automático
   * e dispara o carregamento de instrumentos/registros.
   */
  private finalizarLoadPeriodos(): void {
    if (this.periodo && this.periodos.length > 0) {
      const periodoAtualizado = this.periodos.find(p => p.id === this.periodo!.id);
      if (periodoAtualizado) {
        this.periodo = periodoAtualizado;
      } else {
        this.periodo = this.selectPeriodoAutomatico(this.periodos);
      }
    } else if (!this.periodo && this.periodos.length > 0) {
      this.periodo = this.selectPeriodoAutomatico(this.periodos);
    }

    this.loadInstrumentosTelaAtual().catch(() => undefined);

    if (this.periodo && !this.isStandaloneInstrumentosPage) {
      this.loadRegistrosAvaliacoes();
    }
  }

  /**
   * Seleciona automaticamente o período:
   * 1. Período que contém a data atual
   * 2. Se não houver, o último período (mais recente)
   */
  private selectPeriodoAutomatico(periodos: Periodo[]): Periodo | null {
    return selectPeriodoAutomatico(periodos);
  }

  /**
   * Reinicializa o estado visual da página e dispara as cargas-base do contexto.
   */
  private async initializeComponent(): Promise<void> {
    await this.waitForToken();

    const contexto = this.mobileContextService.getContextoAtual();
    if (!contexto || !contexto.turma || !contexto.turma.id) {
      return;
    }

    // Limpa dados antigos quando reinicializa com novo contexto
    this.colunas = [];
    this.linhas = [];
    this.colunaMediaPeriodo = null;
    this.hasAvaliacao = false;
    this.semDesempenhos = false;
    this.periodos = [];
    this.itensEducacionais = [];
    this.itensEducacionaisTotal = [];
    this.itemEducacional = null;
    this.periodo = null;
    this.enturmacoes = [];
    this.encerramentosTurma = [];
    this.loadingTracker = false;
    this.saveTracker = false;
    this.calculoTracker = false;
    this.isLoadingRegistros = false;
    this.turma = null; // Limpar turma também para forçar atualização

    this.loadContextData();

    if (!this.turma || !(this.turma as any).id) {
      return;
    }

    this.loadPeriodos();
    this.loadConfigurations();
    this.loadEnums();
    this.initializeFilters();
    this.loadFormulasCalculo();
  }

  private async waitForToken(): Promise<void> {
    // window.angular neste domínio é do AngularJS 1.4 do widget cpa-grid, não um app legado
    // esperando handshake — não reintroduzir leitura de token via injector/AuthenticationContext.
    // O token só vem da bridge (localStorage).
    const maxAttempts = APP_CONFIG.TOKEN_WAIT_MAX_ATTEMPTS;
    const delay = APP_CONFIG.TOKEN_WAIT_DELAY_MS;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
      const token = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
      if (token) {
        return;
      }

      await new Promise(resolve => setTimeout(resolve, delay));
    }

    // Após esgotar as tentativas, continua sem token (o interceptor vai tratar)
  }

  private loadFormulasCalculo(): void {
    if (!this.turma?.id) return;
    
    this.desempenhoData.loadFormulasCalculo(this.turma.id.toString(), this.destroy$)
      .subscribe({
        next: (result) => {
          this.formulasCalculo = (result.content || []) as any[];
        },
        error: () => {
          this.formulasCalculo = [];
        }
      });
  }

  /**
   * Lê o contexto disponível na bridge e aplica fallbacks úteis em cenários sem contexto completo.
   */
  private loadContextData(): void {
    this.loadTurmaFromContext();
    this.loadPeriodoFromContext();
    this.loadItensEducacionaisFromContext();
    this.loadItensEducacionaisFromAPI();
    this.handleEmptyContext();
  }

  private loadTurmaFromContext(): void {
    this.turma = this.desempenhoData.loadTurmaFromContext();
  }

  private loadPeriodoFromContext(): void {
    this.periodo = this.desempenhoData.loadPeriodoFromContext();
  }

  private loadItensEducacionaisFromContext(): void {
    this.itensEducacionais = this.desempenhoData.loadItensEducacionaisFromContext();
    this.syncCoresComponentes();
  }

  private loadItensEducacionaisFromAPI(): void {
    if (!this.turma?.id) {
      this.itensEducacionaisTotal = this.itensEducacionais;
      this.selectDisciplinaAutomatica();
      return;
    }

    this.desempenhoData.loadItensEducacionaisFromAPI(this.turma.id, this.destroy$)
      .subscribe({
        next: (itens) => {
          if (itens && itens.length > 0) {
            this.itensEducacionais = itens;
                        this.syncCoresComponentes();
            this.itensEducacionaisTotal = this.itensEducacionais;
            this.selectItemEducacionalFromList();
          }
        },
        error: () => {
          this.itensEducacionaisTotal = this.itensEducacionais;
          this.selectDisciplinaAutomatica();
        }
      });
  }

  private selectItemEducacionalFromList(): void {
    if (this.itensEducacionais.length === 1) {
      this.itemEducacional = this.itensEducacionais[0];
    } else if (this.itensEducacionais.length > 1 && !this.itemEducacional) {
      this.itemEducacional = this.itensEducacionais[0];
    }

    if (this.itemEducacional) {
      this.updateNavigationButtons();
    }

    this.loadInstrumentosTelaAtual().catch(() => undefined);

    if (this.itemEducacional && !this.isStandaloneInstrumentosPage) {
      this.loadRegistrosAvaliacoes();
    }
  }

  private handleEmptyContext(): void {
    if (this.itensEducacionais.length > 0) {
      return;
    }

    this.turma = this.turma || {
      id: 1,
      descricao: '1ª Série A - Manhã',
      tipoAtendimento: 'REGULAR',
      separacaoAlunosEmGrupos: false,
      etapaMatriz: { modular: false }
    };

    this.periodo = this.periodo || {
      id: 1,
      descricao: '1º Bimestre 2025',
      dataInicial: '2025-01-01',
      dataFinal: '2025-04-30'
    };

    this.itensEducacionais = [
      { id: 1, descricao: 'Matemática', isCampoExperiencia: false },
      { id: 2, descricao: 'Português', isCampoExperiencia: false },
      { id: 3, descricao: 'História', isCampoExperiencia: false }
    ];

    this.itensEducacionaisTotal = this.itensEducacionais;
    this.itemEducacional = this.itemEducacional || this.itensEducacionais[0];
  }

  /**
   * Seleciona automaticamente uma disciplina:
   * - Se houver apenas uma, seleciona ela
   * - Se houver várias, seleciona a primeira
   */
  private selectDisciplinaAutomatica(): void {
    if (this.itensEducacionais.length === 1) {
      this.itemEducacional = this.itensEducacionais[0];
      this.updateNavigationButtons();
    } else if (this.itensEducacionais.length > 1 && !this.itemEducacional) {
      this.itemEducacional = this.itensEducacionais[0];
      this.updateNavigationButtons();
    }

    this.loadInstrumentosTelaAtual().catch(() => undefined);

    if (this.itemEducacional && !this.isStandaloneInstrumentosPage) {
      this.loadRegistrosAvaliacoes();
    }
  }

  /**
   * Carrega as configurações que controlam regras de avaliação e visibilidade da tela.
   */
  private loadConfigurations(): void {
    if (!this.turma || !this.turma.id) {
      return;
    }

    this.desempenhoConfig.loadConfiguracaoAvaliacao(
      this.turma.id,
      this.destroy$,
      (config) => {
        this.configuracaoAvaliacao = config;
        this.configuracaoCha = this.desempenhoConfig.createConfiguracaoCha(config);
        this.loadConfiguracaoInstrumentoAvaliacao();
      }
    );
  }

  private loadConfiguracaoInstrumentoAvaliacao(): void {
    if (!this.turma) return;

    this.instrumentoConfiguracaoTurma = this.configuracaoAvaliacao as unknown as Record<string, unknown>;

    this.instrumentoAvaliacaoService.getConfiguracaoEstabelecimento(this.turma.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.instrumentoConfiguracaoEstabelecimento = data;
          this.configuracaoCha = {
            COMPETENCIA: { visivel: !!data?.permiteUtilizarCompetencias },
            CONHECIMENTO: { visivel: !!data?.permiteUtilizarConhecimentosConteudos },
            HABILIDADE: { visivel: !!data?.permiteUtilizarHabilidadesCapacidades },
            ATITUDE: { visivel: !!data?.permiteUtilizarAtitudes }
          };
        },
        error: () => {
          this.instrumentoConfiguracaoEstabelecimento = null;
          this.configuracaoCha = this.desempenhoConfig.createConfiguracaoCha(this.configuracaoAvaliacao);
        }
      });
  }

  private loadEnums(): void {
    this.desempenhoConfig.loadEnums(this.destroy$, (enumName, data) => {
      if (enumName === 'SituacaoMatricula') {
        this.enumSituacaoMatricula = data;
      } else if (enumName === 'SituacaoItemEducacional') {
        this.enumSituacaoDisciplina = data;
      } else if (enumName === 'GrupoEnturmacaoCor') {
        this.grupoEnturmacaoCor = data;
      }
    });
  }

  private initializeFilters(): void {
    this.updateNavigationButtons();
    this.setupSearchSubscription();
  }

  private setupSearchSubscription(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(search => {
        this.searchFilter.keywords = search || '';
        this.showSearchDropdown = (this.searchInputFocused && (!!search || this.listaEnturmacoes.length > 0));
        // Só atualiza o filtro; a lista é filtrada localmente, sem recarregar tudo
      });
  }

  onSearchFocus(): void {
    this.searchInputFocused = true;
    this.showSearchDropdown = true;
  }

  onSearchBlur(): void {
    // Delay para permitir clique no dropdown
    setTimeout(() => {
      this.searchInputFocused = false;
      this.showSearchDropdown = false;
    }, 200);
  }

  selectAluno(linha: LinhaDesempenho | null): void {
    if (linha) {
      this.selectedAluno = linha;
      const nome = this.resolveNomeAluno(linha);
      this.searchControl.setValue(nome, { emitEvent: false });
      this.searchFilter.keywords = nome;
    } else {
      this.selectedAluno = null;
      this.searchControl.setValue('', { emitEvent: false });
      this.searchFilter.keywords = '';
    }
    this.showSearchDropdown = false;
  }

  isAlunoSelecionado(linha: LinhaDesempenho): boolean {
    return !!this.selectedAluno && this.selectedAluno.enturmacao.id === linha.enturmacao.id;
  }

  limparBusca(): void {
    this.selectedAluno = null;
    this.searchControl.setValue('', { emitEvent: true });
    this.searchFilter.keywords = '';
    this.showSearchDropdown = false;
    this.filtroSituacaoEnturmacao = null;
    this.loadRegistrosAvaliacoes();
  }

  onSituacaoEnturmacaoChange(valor: string | null): void {
    this.filtroSituacaoEnturmacao = valor;
    this.loadRegistrosAvaliacoes();
  }

  get alunosParaBusca(): LinhaDesempenho[] {
    if (!this.linhas || this.linhas.length === 0) {
      return [];
    }
    
    const searchText = this.searchControl.value?.toLowerCase().trim() || '';
    
    if (!searchText) {
      return this.linhas.slice(0, 10); // Mostra apenas os primeiros 10 quando não há busca
    }
    
    return this.linhas.filter(linha => {
      const nome = this.resolveNomeAluno(linha).toLowerCase();
      const numeroChamada = linha.enturmacao.numeroChamada?.toString() || '';
      return nome.includes(searchText) || numeroChamada.includes(searchText);
    }).slice(0, 10); // Limita a 10 resultados
  }

  filtraEnturmacoes(): void {
    if (!this.turma) return;

    // loadRegistrosAvaliacoes já gerencia o loadingTracker e isLoadingRegistros
    this.loadEnturmacoes().then(() => {
      this.loadRegistrosAvaliacoes();
    });
  }

  atualizarLista(): void {
    this.filtraEnturmacoes();
  }

  salvar(nota: NotaView | (LinhaDesempenho & { notaMediaPeriodo?: NotaView; notas?: NotaView[] })): void {
    if (!this.turma || !nota) return;

    const notaView = resolveNotaParaSalvar(nota) as NotaViewCompat | null;
    if (!notaView) return;

    notaView.$$mensagemValidacao = null;

    const validationMessage = this.validateNotaBeforeSave(notaView);
    if (validationMessage) {
      const isErroIntervalo = validationMessage.includes('maior ou igual') || 
                              validationMessage.includes('menor ou igual') || 
                              validationMessage.includes('estar entre') ||
                              validationMessage.includes('negativas');

      if (this.porInstrumento && isErroIntervalo) {
        notaView.$$mensagemValidacao = validationMessage;
      } else {
        this.notificationService.warning({ title: 'Atenção', message: validationMessage });
      }
      return;
    }

    convertNotaValue(notaView, this.configuracaoAvaliacao);
    clearFields(notaView);

    notaView.$$isSaving = true;
    this.saveTracker = true;
    const skipContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

    this.notaService.salvarNota(this.turma.id, notaView, skipContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (novaNota) => {
          const notaSemIdAntesSalvar = !notaView.id;
          this.successfulSave = true;
          notaView.$$isSaving = false;
          notaView.id = novaNota.id;

          notaView.notaNumerica = novaNota.notaNumerica;
          notaView.notaDescritiva = novaNota.notaDescritiva;
          notaView.notaConceito = novaNota.notaConceito;
          notaView.avaliavel.publicavel = novaNota.avaliavel?.publicavel;
          notaView.avaliavel.publicado = novaNota.avaliavel?.publicado;
          notaView.avaliavel.dataPublicacao = novaNota.avaliavel?.dataPublicacao;
          notaView.$$mensagemValidacao = null;
          
          if (notaView.updateFields) {
            notaView.updateFields();
          }
          
          if (this.calculoFormula && !isNotaMediaPeriodo(notaView)) {
            this.calculoFormula.alterado = true;
          }

          if (notaSemIdAntesSalvar && !novaNota.id) {
            this.atualizarLista();
          }

          this.saveTracker = false;
        },
        error: (err: any) => {
          notaView.$$isSaving = false;
          this.saveTracker = false;
          
          let mensagemBruta = 'Não foi possível salvar o desempenho.';

          if (err.error?.detail && typeof err.error.detail === 'object') {
            const valoresDetalhe = Object.values(err.error.detail);
            mensagemBruta = valoresDetalhe.length > 0 ? String(valoresDetalhe[0]) : mensagemBruta;
          } else if (err.error?.message) {
            mensagemBruta = err.error.message;
          }

          const mensagemLimpa = mensagemBruta
            .replace(/<[^>]*>/g, '')
            .replace(/[`]/g, '')
            .trim();

          const isErroIntervaloAPI = mensagemLimpa.includes('maior ou igual') || 
                                     mensagemLimpa.includes('menor ou igual') || 
                                     mensagemLimpa.includes('estar entre');

          if (this.porInstrumento && isErroIntervaloAPI) {
            notaView.$$mensagemValidacao = mensagemLimpa;
          } else {
            notaView.$$mensagemValidacao = null;
            this.notificationService.error({ 
              title: 'Atenção',
              message: mensagemLimpa
            });
          }
        }
      });
  }

  private validateNotaBeforeSave(nota: NotaView): string | null {
    if (this.calculoFormula?.status === 'PROCESSANDO') {
      return 'Não é permitido alterar registros enquanto o cálculo da média dos instrumentos estiver em processamento.';
    }

    const modoAvaliacao = resolveModoAvaliacaoNota(nota);
    if (modoAvaliacao === 'NUMERICO') {
      const valor = Number(String(nota.$$nota || '').replace(',', '.'));
      if (!Number.isNaN(valor) && valor < 0) {
        return 'Não é permitido registrar notas negativas.';
      }

      const notaMinima = this.resolveNotaMinima(nota);
      const notaMaxima = this.resolveNotaMaxima(nota);
      if (!Number.isNaN(valor) && notaMinima !== null && valor < notaMinima) {
        return `A nota deve ser maior ou igual a ${notaMinima}.`;
      }
      if (!Number.isNaN(valor) && notaMaxima !== null && valor > notaMaxima) {
        return `A nota deve ser menor ou igual a ${notaMaxima}.`;
      }
    }

    if (modoAvaliacao === 'DESCRITIVO' && String(nota.$$nota || '').length > 16000) {
      return 'O registro descritivo deve possuir no máximo 16000 caracteres.';
    }
    return null;
  }

  private buildSaveErrorMessage(error: unknown, nota: NotaView): string {
    const status = (error as { status?: number } | null)?.status;
    const intervaloNota = this.getMensagemIntervaloNota(nota);

    if (this.isSituacaoBloqueada(nota)) {
      return `Não é permitido alterar a nota de aluno com situação ${getSituacaoDescricaoCurta(nota.enturmacao.situacao ?? nota.enturmacao.matricula?.situacao ?? undefined)}.`;
    }

    if (status === 422) {
      const backendMessage = this.errorHandler.extractErrorMessageComplete(error);

      if (!backendMessage || backendMessage.includes('Http failure response')) {
        return intervaloNota || 'Não foi possível salvar a nota porque o valor informado não atende às regras do instrumento ou do período avaliativo.';
      }

      return backendMessage;
    }

    return this.errorHandler.extractErrorMessageComplete(error) || 'Erro ao salvar nota';
  }

  private isSituacaoBloqueada(nota: NotaView): boolean {
    let situacaoRaw = nota.enturmacao?.situacao || nota.enturmacao?.matricula?.situacao || '';

    // Bloqueia edição se a situação específica da disciplina (não a geral da matrícula) for de dispensa/remanejamento
    const situacoesItens = (nota.enturmacao as any)?.situacaoItensEducacionais;
    if (this.itemEducacional && Array.isArray(situacoesItens)) {
      const sitEspecifica = situacoesItens.find((s: any) => s.itemEducacional?.id === this.itemEducacional?.id);
      if (sitEspecifica && sitEspecifica.situacao) {
        situacaoRaw = sitEspecifica.situacao;
      }
    }

    const situacao = String(situacaoRaw).toUpperCase();
    return ['DISPENSADO', 'DISPENSADO_PARCIAL', 'REMANEJADO'].includes(situacao);
  }

  private isNotaDataFutura(nota: NotaView): boolean {
    const data = this.parseDate(String(nota.avaliavel?.data || ''));
    const hoje = this.parseDate(getHojeISO());
    return !!(data && hoje && data.getTime() > hoje.getTime());
  }

  private isNotaBloqueadaPorEncerramento(nota: NotaView): boolean {
    const tipoPeriodo = nota.avaliavel?.tipoPeriodo;
    const periodoAvaliativoId = Number((nota.avaliavel?.periodoAvaliativo as { id?: number } | undefined)?.id || 0);

    // Tipos vinculados ao ano letivo inteiro: bloqueio só quando o letivo está encerrado
    if (tipoPeriodo === 'MEDIA_FINAL' || tipoPeriodo === 'CONSELHO_CLASSE' || tipoPeriodo === 'EXAME_FINAL') {
      return this.isPeriodoLetivoEncerrado(this.encerramentosTurma as Array<Record<string, unknown>>);
    }

    if (periodoAvaliativoId > 0) {
      const periodo = this.periodos.find((item) => item.id === periodoAvaliativoId) || null;
      return !!(periodo && this.isPeriodoEncerrado(periodo));
    }

    return false;
  }

  private resolveNotaMinima(nota: NotaView): number | null {
    const avaliavel = nota.avaliavel as ItemAvaliavel & { notaMinima?: number };
    const notaMinimaAvaliavel = parseUnknownNumber(avaliavel.notaMinima);
    if (notaMinimaAvaliavel !== null) {
      return notaMinimaAvaliavel;
    }

    const instrumento = this.findInstrumentoResumoForNota(nota);
    if (typeof instrumento?.notaMinima === 'number') {
      return instrumento.notaMinima;
    }

    return 0;
  }

  private resolveNotaMaxima(nota: NotaView): number | null {
    const avaliavel = nota.avaliavel as ItemAvaliavel & { notaMaxima?: number };
    const notaMaximaAvaliavel = parseUnknownNumber(avaliavel.notaMaxima);
    if (notaMaximaAvaliavel !== null) {
      return notaMaximaAvaliavel;
    }

    const instrumento = this.findInstrumentoResumoForNota(nota);
    if (typeof instrumento?.notaMaxima === 'number') {
      return instrumento.notaMaxima;
    }

    return null;
  }

  private getMensagemIntervaloNota(nota: NotaView): string | null {
    const notaMinima = this.resolveNotaMinima(nota);
    const notaMaxima = this.resolveNotaMaxima(nota);

    if (notaMinima !== null && notaMaxima !== null) {
      return `A nota informada deve estar entre ${notaMinima} e ${notaMaxima}.`;
    }

    if (notaMinima !== null && notaMinima > 0) {
      return `A nota informada deve ser maior ou igual a ${notaMinima}.`;
    }

    if (notaMaxima !== null) {
      return `A nota informada deve ser menor ou igual a ${notaMaxima}.`;
    }

    return null;
  }

  private findInstrumentoResumoForNota(nota: NotaView): InstrumentoResumoView | null {
    const avaliavelId = Number(nota.avaliavel?.id || 0);

    if (avaliavelId > 0) {
      const instrumento = this.instrumentosResumo.find((item) => item.id === avaliavelId) || null;
      if (instrumento) {
        return instrumento;
      }
    }

    return this.instrumentoVisualizadoResumo;
  }

  openPorInstrumento(): void {
    this.porInstrumento = true;
    if (this.turma && !this.isStandaloneInstrumentosPage) {
      this.loadRegistrosAvaliacoes();
    }
  }

  openPorAno(): void {
    this.porInstrumento = false;
    if (this.turma) {
      this.loadRegistrosAvaliacoes();
    }
  }

  selecionarInstrumentoVisualizado(instrumentoId: number | null): void {
    this.instrumentosFacade.selecionarInstrumentoVisualizado(instrumentoId);
    this.showInstrumentoDropdown = false;
  }

  selecionarInstrumentoAnterior(): void {
    (document.activeElement as HTMLElement)?.blur();
    this.instrumentosFacade.selecionarInstrumentoAnterior(this.instrumentosAvaliacaoParaSelecao);
    this.showInstrumentoDropdown = false;
  }

  selecionarProximoInstrumento(): void {
    (document.activeElement as HTMLElement)?.blur();
    this.instrumentosFacade.selecionarProximoInstrumento(this.instrumentosAvaliacaoParaSelecao);
    this.showInstrumentoDropdown = false;
  }

  fecharParecerModal(): void {
    if (this.parecerModalSalvando) {
      return;
    }

    this.parecerModalAberto = false;
    this.parecerModalCarregando = false;
    this.parecerModalTexto = '';
    this.parecerModalNota = null;
  }

  salvarParecerModal(): void {
    if (!this.parecerModalNota) {
      return;
    }

    if (this.parecerModalTexto.length > 32000) {
      this.errorMessage = 'O parecer deve possuir no máximo 32000 caracteres.';
      this.showErrorModal = true;
      return;
    }

    this.parecerModalSalvando = true;
    const parecerNota = this.parecerModalNota as NotaViewCompat;
    parecerNota.parecer = this.parecerModalTexto;
    parecerNota.parecerDetalhadoConteudo = this.parecerModalTexto;
    this.salvar(parecerNota);
    this.parecerModalSalvando = false;
    this.fecharParecerModal();
  }

  fecharAvaliacaoDescritivaModal(): void {
    this.avaliacaoDescritivaModalAberto = false;
    this.avaliacaoDescritivaModalTexto = '';
    this.avaliacaoDescritivaModalNota = null;
  }

  salvarAvaliacaoDescritivaModal(): void {
    if (!this.avaliacaoDescritivaModalNota) {
      return;
    }

    const avaliacaoNota = this.avaliacaoDescritivaModalNota as NotaViewCompat;
    avaliacaoNota.$$nota = this.avaliacaoDescritivaModalTexto;
    avaliacaoNota.$$hasChanged = true;
    avaliacaoNota.$$mensagemValidacao = null;

    this.salvar(avaliacaoNota);
    this.fecharAvaliacaoDescritivaModal();
  }

  togglePublicacaoAvaliacao(nota: NotaView | unknown): void {
    const notaView = nota as NotaView | null;
    if (!notaView) {
      return;
    }

    if (this.isPublicacaoAvaliacaoBloqueada(notaView)) {
      this.notificationService.warning({
        title: 'Publicação bloqueada',
        message: 'Não é permitido publicar ou desfazer a publicação dos desempenhos dos alunos quando o período avaliativo ou o período letivo estiver encerrado.'
      });
      return;
    }

    const publicado = this.isAvaliacaoPublicada(notaView);
    
    if (!publicado) {
      this.avaliacaoConfirmacaoNota = notaView;
      this.avaliacaoConfirmacaoPublicar = true;
      this.confirmarAcaoAvaliacao();
    } else {
      this.avaliacaoConfirmacaoNota = notaView;
      this.avaliacaoConfirmacaoPublicar = false;
      this.avaliacaoConfirmacaoTitulo = 'Desfazer publicação';
      this.avaliacaoConfirmacaoAberta = true;
    }
  }

  fecharConfirmacaoAvaliacao(): void {
    this.avaliacaoConfirmacaoAberta = false;
    this.avaliacaoConfirmacaoNota = null;
    this.avaliacaoConfirmacaoPublicar = false;
    this.avaliacaoConfirmacaoTitulo = '';
  }

  confirmarAcaoAvaliacao(): void {
    const nota = this.avaliacaoConfirmacaoNota;
    let itemAvaliavelId: number;
    
    // Verifica se é o fluxo novo (via triggerConfirmacaoDespublicar) ou antigo
    if (this.avaliacaoPublicandoId && !nota) {
      itemAvaliavelId = this.avaliacaoPublicandoId;
    } else {
      itemAvaliavelId = Number(nota?.avaliavel?.id || 0);
    }
    
    const turmaId = this.turma?.id || 0;

    if (itemAvaliavelId <= 0 || turmaId <= 0) {
      this.fecharConfirmacaoAvaliacao();
      return;
    }

    this.avaliacaoService.publicarItemAvaliavel(turmaId, itemAvaliavelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const publicado = !!(response?.publicavel?.publicado ?? response?.publicado ?? this.avaliacaoConfirmacaoPublicar);
          const dataPublicacao = response?.publicavel?.dataPublicacao || response?.dataPublicacao || null;
          this.atualizarEstadoPublicacaoAvaliacao(itemAvaliavelId, publicado, dataPublicacao);
          this.avaliacaoPublicandoId = null;
          this.fecharConfirmacaoAvaliacao();
          
          if (nota) {
            this.notificationService.success({
              title: publicado ? 'Desempenho publicado' : 'Publicação desfeita',
              message: getMensagemSucessoPublicacaoAvaliacao(nota, publicado)
            });
          } else {
            this.notificationService.success({
              title: publicado ? 'Item publicado' : 'Publicação desfeita',
              message: publicado ? 'Item publicado com sucesso.' : 'Publicação desfeita com sucesso.'
            });
          }
        },
        error: (error) => {
          this.avaliacaoPublicandoId = null;
          this.fecharConfirmacaoAvaliacao();
          this.errorHandler.handleError(error);
        }
      });
  }

  isPublicacaoAvaliacaoBloqueada(nota: NotaView): boolean {
    if (!nota?.avaliavel?.id || !this.turma?.id) {
      return true;
    }

    if (!this.isNotaComConteudoPublicavel(nota) && !this.isAvaliacaoPublicada(nota)) {
      return true;
    }

    return this.isNotaBloqueadaPorEncerramento(nota);
  }

  private isNotaComConteudoPublicavel(nota: NotaView): boolean {
    return isNotaComConteudoPublicavel(nota);
  }

  isAvaliacaoPublicada(nota: NotaView): boolean {
    return isAvaliacaoPublicada(nota);
  }

  getDataPublicacaoAvaliacao(nota: NotaView | null): string | null {
    return getDataPublicacaoAvaliacao(nota);
  }

  getRotuloAvaliacaoPublicacao(nota: NotaView | null): string {
    return getRotuloAvaliacaoPublicacao(nota);
  }

  private atualizarEstadoPublicacaoAvaliacao(itemAvaliavelId: number, publicado: boolean, dataPublicacao: string | null): void {
    const atualizarAvaliavel = (avaliavel?: ItemAvaliavel | null): void => {
      if (!avaliavel || Number(avaliavel.id || 0) !== itemAvaliavelId) {
        return;
      }

      avaliavel.publicavel = {
        ...(avaliavel.publicavel || {}),
        publicado,
        dataPublicacao
      };
      avaliavel.publicado = publicado;
      avaliavel.dataPublicacao = dataPublicacao;
    };

    (this.colunas || []).forEach((coluna) => atualizarAvaliavel(coluna.rawValue as ItemAvaliavel | undefined));
    (this.linhas || []).forEach((linha) => {
      atualizarAvaliavel(linha.notaMediaPeriodo?.avaliavel);
      (linha.notas || []).forEach((item) => atualizarAvaliavel(item.avaliavel));
    });
  }

  /**
   * Abre o modal de instrumento com o contexto atual já refletido no formulário.
   */
  async openCadInstrumentoAvaliacao(): Promise<void> {
    this.instrumentoRegistroOriginal = null;
    this.instrumentosFacade.abrirCadastro({
      turmaId: this.turma?.id || null,
      itemEducacionalId: this.itemEducacional?.id || null,
      periodoAvaliativoId: this.periodo?.id || null
    });
    this.resetInstrumentoCampoAdicional();
    this.syncInstrumentoFormWithContext();

    await Promise.all([
      this.loadDescricoesRecentesInstrumento(),
      this.carregarDadosFormularioInstrumento()
    ]);
    await this.carregarCampoAdicionalInstrumento(true);

    this.instrumentoSnapshotInicial = this.serializeInstrumentoForm();
    this.instrumentoArquivosSnapshotInicial = this.serializeInstrumentoArquivos();
    this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
    this.instrumentoCarregando = false;
    this.instrumentoMostrarErros = false;
  }

  fecharFormularioInstrumento(): void {
    this.instrumentosFacade.fecharFormulario();
    this.resetInstrumentoCampoAdicional();
    this.closeInstrumentoFormulario$.next();
    this.closeInstrumentoFormulario$ = new Subject<void>();
  }

  selecionarDescricaoRecenteInstrumento(descricao: string): void {
    this.instrumentosFacade.selecionarDescricaoRecente(descricao);
  }

  /**
   * Abre o modal em modo de edição preservando o DTO original para atualização posterior.
   */
  editarInstrumento(instrumento: InstrumentoResumoView): void {
    if (!instrumento?.id) {
      return;
    }

    if (!this.podeEditarInstrumento(instrumento)) {
      return;
    }

    this.instrumentosFacade.abrirEdicao();
    this.resetInstrumentoCampoAdicional();

    Promise.all([
      this.loadDescricoesRecentesInstrumento(),
      new Promise<void>((resolve, reject) => {
        this.instrumentoAvaliacaoService.get(instrumento.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: async (registro) => {
              const registroOriginal = {
                ...registro
              } as InstrumentoAvaliacaoRegistro;

              // A listagem pode trazer metadados de concorrência que não retornam no detalhe.
              if (registroOriginal.version === undefined || registroOriginal.version === null) {
                registroOriginal.version = (instrumento.raw as { version?: number | null }).version ?? null;
              }

              if (registroOriginal.versao === undefined || registroOriginal.versao === null) {
                registroOriginal.versao = (instrumento.raw as { versao?: number | null }).versao ?? null;
              }

              this.instrumentoRegistroOriginal = registroOriginal;
              this.instrumentoForm = this.mapInstrumentoToForm(registroOriginal);
              if (this.instrumentoForm.instrumentoRecuperacaoId) {
                this.preloadInstrumentoRecuperacaoDetalhes(this.instrumentoForm.instrumentoRecuperacaoId);
              }
              this.instrumentoArquivos = this.mapInstrumentoArquivos(registroOriginal['arquivos'] as InstrumentoArquivoAnexo[] | null | undefined);
              this.instrumentoCampoAdicional = null;
              await this.carregarDadosFormularioInstrumento(registro);
              await this.carregarCampoAdicionalInstrumento();
              this.instrumentoSnapshotInicial = this.serializeInstrumentoForm();
              this.instrumentoArquivosSnapshotInicial = this.serializeInstrumentoArquivos();
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCarregando = false;
              resolve();
            },
            error: (error) => {
              this.instrumentoCarregando = false;
              this.instrumentoFormularioAberto = false;
              this.errorHandler.handleError(error);
              reject(error);
            }
          });
      })
    ]).catch(() => {
      this.instrumentoCarregando = false;
    });
  }

  salvarInstrumento(adicionarNovo = false): void {
    if (this.instrumentoAnexosEnviando) {
      this.notificationService.warning({
        title: 'Aguarde o envio',
        message: 'Finalize o upload dos anexos antes de salvar o instrumento.'
      });
      return;
    }

    const erros = this.validarFormularioInstrumento();
    this.instrumentoMostrarErros = true;

    if (erros.length) {
      this.notificationService.warning({
        title: 'Revise o formulário',
        message: erros[0]
      });
      return;
    }

    const payload = this.buildInstrumentoPayload();
    this.instrumentoSalvando = true;

    const recuperacaoVinculada = this.getRecuperacaoParalelaVinculada(this.instrumentoForm.id || null);
    if (this.deveAtualizarRecuperacaoVinculada(payload, recuperacaoVinculada)) {
      this.salvarInstrumentoComRecuperacaoVinculada(payload, recuperacaoVinculada!, adicionarNovo);
      return;
    }

    this.instrumentoAvaliacaoService.save(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.finalizarSucessoInstrumento(adicionarNovo),
        error: (error) => {
          this.instrumentoSalvando = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  confirmarExclusaoInstrumento(instrumento: InstrumentoResumoView): void {
    this.instrumentosFacade.abrirConfirmacaoExclusao(instrumento);
  }

  confirmarExclusaoInstrumentoEmEdicao(): void {
    const instrumento = this.instrumentosTelaResumo.find((item) => item.id === this.instrumentoForm.id);
    if (!instrumento) {
      return;
    }
    this.fecharFormularioInstrumento();
    this.confirmarExclusaoInstrumento(instrumento);
  }

  togglePublicacaoInstrumento(instrumento: InstrumentoResumoView): void {
    if (!this.podeAlternarPublicacaoInstrumento(instrumento)) {
      return;
    }

    const publicado = instrumento.publico;

    if (!publicado) {
      this.instrumentoConfirmacao = {
        aberta: false,
        tipo: 'publicacao',
        instrumento: instrumento,
        publicar: true,
        titulo: 'Publicar instrumento',
        confirmadoExclusao: false
      };
      this.confirmarAcaoInstrumento();
    } else {
      this.instrumentosFacade.abrirConfirmacaoPublicacao(
        instrumento,
        false,
        'Desfazer publicação'
      );
    }
  }

  fecharConfirmacaoInstrumento(): void {
    this.instrumentosFacade.fecharConfirmacao();
  }

  confirmarAcaoInstrumento(): void {
    const instrumento = this.instrumentoConfirmacao.instrumento;

    if (!instrumento?.id) {
      this.fecharConfirmacaoInstrumento();
      return;
    }

    const instrumentoSelecionado = instrumento;

    const instrumentoId = instrumento.id;

    if (this.instrumentoConfirmacao.tipo === 'excluir') {
      if (!this.instrumentoConfirmacao.confirmadoExclusao) {
        this.notificationService.warning({
          title: 'Confirmação necessária',
          message: 'Marque a confirmação para continuar com a exclusão do instrumento.'
        });
        return;
      }

      this.fecharConfirmacaoInstrumento();

      if (!this.podeExcluirInstrumento(instrumento)) {
        return;
      }

      this.instrumentoSalvando = true;
      this.instrumentoAvaliacaoService.remove(instrumento.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.instrumentoSalvando = false;
            this.notificationService.info({
              title: 'Instrumento excluído',
              message: 'O instrumento de avaliação foi removido com sucesso.'
            });
            this.atualizaListaAvaliacao();
            this.fecharFormularioInstrumento();
          },
          error: (error) => {
            this.instrumentoSalvando = false;
            this.errorHandler.handleError(error);
          }
        });
      return;
    }

    const turmaId = instrumento.turmaId || this.turma?.id || Number(((instrumento.raw?.['turma'] as { id?: number } | undefined)?.id) || 0) || null;

    if (!turmaId) {
      this.instrumentoPublicandoId = null;
      this.fecharConfirmacaoInstrumento();
      this.notificationService.warning({
        title: 'Publicação indisponível',
        message: 'Não foi possível identificar a turma do instrumento para concluir a publicação.'
      });
      return;
    }

    this.instrumentoPublicandoId = instrumento.id;
    this.instrumentoAvaliacaoService.publicar(turmaId, instrumento.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.instrumentoPublicandoId = null;
          this.fecharConfirmacaoInstrumento();
          const publicado = !!(response?.publicavel?.publicado ?? response?.publicado ?? this.instrumentoConfirmacao.publicar);
          this.atualizarEstadoPublicacaoInstrumento(instrumentoId, publicado, response?.dataPublicacao || response?.publicavel?.dataPublicacao || null);
          this.notificationService.success({
            title: publicado ? 'Instrumento publicado' : 'Publicação desfeita',
            message: this.getMensagemSucessoPublicacaoInstrumento(instrumentoSelecionado, publicado)
          });
        },
        error: (error) => {
          this.instrumentoPublicandoId = null;
          this.fecharConfirmacaoInstrumento();
          this.errorHandler.handleError(error);
        }
      });
  }

  private syncInstrumentoFormWithContext(): void {
    this.instrumentoForm.turmaId = this.instrumentoForm.turmaId || this.turma?.id || null;
    this.instrumentoForm.itemEducacionalId = this.instrumentoForm.itemEducacionalId || this.itemEducacional?.id || null;
    this.instrumentoForm.periodoAvaliativoId = this.instrumentoForm.periodoAvaliativoId || this.periodo?.id || null;
  }

  private loadDescricoesRecentesInstrumento(): Promise<void> {
    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getDescricoesRecentes(5)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (descricoes) => {
            this.instrumentoDescricoesRecentes = descricoes || [];
            resolve();
          },
          error: () => {
            this.instrumentoDescricoesRecentes = [];
            resolve();
          }
        });
    });
  }

  /**
   * Carrega os períodos válidos do item educacional e ajusta o formulário conforme o tipo de turma.
   */
  private loadInstrumentoPeriodos(): Promise<void> {
    if (!this.instrumentoForm.itemEducacionalId) {
      this.instrumentoPeriodos = [];
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getPeriodosItemAvaliacao(this.instrumentoForm.itemEducacionalId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (periodos) => {
            this.instrumentoPeriodos = periodos || [];

            if (this.instrumentoTurmaEAtividade && this.instrumentoPeriodos.length) {
              this.instrumentoForm.modoAvaliacao = this.instrumentoPeriodos[0].modoAvaliacao || undefined;
            }

            if (!this.instrumentoTurmaEAtividade && this.instrumentoForm.periodoAvaliativoId) {
              const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
              this.instrumentoForm.modoAvaliacao = periodoDetalhado?.modoAvaliacao || undefined;
            }

            if (this.instrumentoTurmaEAtividade) {
              this.instrumentoForm.periodoAvaliativoId = null;
            } else if (!this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId)) {
              const primeiroPeriodo = this.instrumentoPeriodos[0];
              this.instrumentoForm.periodoAvaliativoId = Number(primeiroPeriodo?.periodoAvaliativo?.id ?? primeiroPeriodo?.id ?? 0) || null;
            }

            resolve();
          },
          error: () => {
            this.instrumentoPeriodos = [];
            resolve();
          }
        });
    });
  }

  onInstrumentoAnexosSelecionados(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);

    if (!files.length) {
      return;
    }

    this.processarInstrumentoAnexos(files);

    if (input) {
      input.value = '';
    }
  }

  onInstrumentoArquivoSelecionado(file: File): void {
    this.processarInstrumentoAnexos([file]);
  }

  onInstrumentoArquivosSelecionados(files: File[]): void {
    this.processarInstrumentoAnexos(files);
  }

  baixarInstrumentoAnexoCompartilhado(anexo: AnexoItem): void {
    const arquivo = this.instrumentoArquivos.find((item, index) => this.resolveInstrumentoArquivoIdentificador(item, index) === anexo.id);

    if (!arquivo) {
      this.notificationService.warning({
        title: 'Download indisponível',
        message: 'O arquivo informado não foi encontrado.'
      });
      return;
    }

    this.baixarInstrumentoAnexo(arquivo);
  }

  removerInstrumentoAnexoCompartilhado(anexoId: number | string): void {
    this.instrumentoArquivos = this.instrumentoArquivos.filter(
      (arquivo, index) => this.resolveInstrumentoArquivoIdentificador(arquivo, index) !== anexoId
    );
  }

  onInstrumentoTurmaSelectChange(value: number | string | null): void {
    this.instrumentoForm.turmaId = this.toNullableNumber(value);
    this.onInstrumentoTurmaChange();
  }

  onInstrumentoItemEducacionalSelectChange(value: number | string | null): void {
    this.instrumentoForm.itemEducacionalId = this.toNullableNumber(value);
    this.onInstrumentoItemEducacionalChange();
  }

  onInstrumentoPeriodoSelectChange(value: number | string | null): void {
    this.instrumentoForm.periodoAvaliativoId = this.toNullableNumber(value);
    this.onInstrumentoPeriodoChange();
  }

  onInstrumentoTipoSelectChange(value: number | string | null): void {
    this.instrumentoForm.tipoInstrumento = typeof value === 'string'
      ? value as TipoInstrumentoAvaliacaoId
      : null;
    this.onInstrumentoTipoChange();
  }

  onInstrumentoRecuperacaoSelectChange(value: number | string | null): void {
    this.instrumentoForm.instrumentoRecuperacaoId = this.toNullableNumber(value);
    this.onInstrumentoRecuperacaoChange();
  }

  onInstrumentoAbaChange(tabId: string): void {
    if (tabId === 'informacoes' || tabId === 'anexos' || tabId === 'dadosAdicionais') {
      this.instrumentoAbaAtiva = tabId;

      if (tabId === 'dadosAdicionais') {
        this.carregarCampoAdicionalInstrumento().catch(() => undefined);
      }
    }
  }

  private processarInstrumentoAnexos(files: File[]): void {
    const arquivosValidos = files.filter((file) => this.validarInstrumentoAnexo(file));

    if (!arquivosValidos.length) {
      return;
    }

    this.instrumentoAnexosEnviando = true;
    let uploadsPendentes = arquivosValidos.length;

    arquivosValidos.forEach((file) => {
      this.instrumentoAvaliacaoService.uploadAnexo(file)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            uploadsPendentes -= 1;
            this.instrumentoAnexosEnviando = uploadsPendentes > 0;
          })
        )
        .subscribe({
          next: (response) => {
            this.instrumentoArquivos = [
              ...this.instrumentoArquivos,
              this.mapInstrumentoArquivo(response, file.name, file.size, file.type)
            ];
          },
          error: (error) => {
            this.errorHandler.handleError(error);
          }
        });
    });
  }

  baixarInstrumentoAnexo(anexo: InstrumentoArquivoState): void {
    if (!anexo?.id) {
      this.notificationService.warning({
        title: 'Download indisponível',
        message: 'O arquivo ainda não está disponível para download.'
      });
      return;
    }

    const downloadUrl = this.instrumentoAvaliacaoService.getDownloadAnexoUrl(anexo.id);

    if (!downloadUrl) {
      this.notificationService.warning({
        title: 'Download indisponível',
        message: 'Não foi possível obter as credenciais para baixar o anexo.'
      });
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.download = anexo.nome || 'anexo';
    anchor.click();
  }

  removerInstrumentoAnexo(index: number): void {
    this.instrumentoArquivos = this.instrumentoArquivos.filter((_, arquivoIndex) => arquivoIndex !== index);
  }

  get instrumentoAnexosAccept(): string {
    return buildInstrumentoAnexosAccept(INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS);
  }

  /**
   * Monta o payload final do modal preservando a estrutura esperada pelo backend.
   */

  private buildInstrumentoPayload(): InstrumentoAvaliacaoRegistro {
    const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
    const instrumentoRecuperacao = this.instrumentosRecuperacaoOptions.find((item) => item.id === this.instrumentoForm.instrumentoRecuperacaoId) || null;

    const payloadBase = construirInstrumentoPayload(this.instrumentoForm, {
      registroOriginal: this.instrumentoRegistroOriginal,
      periodoDetalhado,
      periodoSelecionadoFallback: this.instrumentoPeriodoSelecionado,
      shouldShowPeriodo: this.shouldShowInstrumentoPeriodoField,
      turmaSelecionada: this.instrumentoTurmaSelecionada,
      itemSelecionado: this.instrumentoItemSelecionado,
      turmaEAtividade: this.instrumentoTurmaEAtividade,
      primeiroPeriodoTipoPeriodo: this.instrumentoPeriodos[0]?.tipoPeriodo,
      instrumentoRecuperacao,
      modoAvaliacaoFallback: this.getModoAvaliacaoInstrumentoAtual(),
      instrumentoRecuperacaoDetalhado: instrumentoRecuperacao?.id ? this.instrumentoRecuperacaoDetalhes.get(instrumentoRecuperacao.id) : undefined,
      exibeFaixaNotas: this.instrumentoExibeFaixaNotas,
      exibePeso: this.instrumentoExibePeso,
      instrumentoArquivos: this.instrumentoArquivos,
      campoAdicionalOriginal: this.instrumentoCampoAdicional
    });

    this.normalizarPayloadRecuperacaoParalela(payloadBase);

    return payloadBase;
  }

  /**
   * Consolida as regras de negócio do modal antes de salvar ou atualizar.
   */
  private validarFormularioInstrumento(): string[] {
    const data = this.parseDate(this.instrumentoForm.data);
    // Só chama o serviço de calendário quando há data válida — mesma condição do `if (data)`
    // original, pra não disparar a verificação de dia letivo sem necessidade.
    const diaLetivoValido = data
      ? this.calendarioService.verificaDiaLetivo(data, this.instrumentoCalendario, this.getPeriodoSelecionadoParaValidacaoLetiva(), undefined)
      : true;

    return validarInstrumentoFormulario(this.instrumentoForm, {
      labelItemEducacional: this.labelItensEducacionais,
      shouldShowPeriodo: this.shouldShowInstrumentoPeriodoField,
      turmaEjaModular: this.instrumentoTurmaEjaModular,
      turmaEAtividade: this.instrumentoTurmaEAtividade,
      instrumentosContexto: this.instrumentoContextoResumo,
      instrumentosRecuperacaoOptions: this.instrumentosRecuperacaoOptions,
      exibeFaixaNotas: this.instrumentoExibeFaixaNotas,
      exibePeso: this.instrumentoExibePeso,
      notaMinimaConfiguracao: (this.instrumentoConfiguracaoTurma as any)?.['notaMinima'] ?? (this.instrumentoConfiguracaoEstabelecimento as any)?.['notaMinima'] ?? 0,
      notaMaximaConfiguracao: (this.instrumentoConfiguracaoTurma as any)?.['notaMaxima'] ?? (this.instrumentoConfiguracaoEstabelecimento as any)?.['notaMaxima'] ?? 10,
      pesoMinimoLimite: this.instrumentoPesoMinimoLimite,
      pesoMaximoLimite: this.instrumentoPesoMaximoLimite,
      rangePeriodoSelecionado: this.getRangePeriodoSelecionado(),
      diaLetivoValido,
      possuiNotasInformadas: this.instrumentoForm.id ? this.instrumentoPossuiNotasInformadas(this.instrumentoForm.id) : false,
      alteracoesCriticas: this.getAlteracoesCriticasInstrumento(),
      alteracoesFormulario: this.getAlteracoesFormularioInstrumento(),
      anoLetivoAtual: this.getAnoLetivoAtual(),
      recuperacaoParalelaVinculada: this.instrumentoForm.id ? this.getRecuperacaoParalelaVinculada(this.instrumentoForm.id) : undefined,
      camposAdicionaisObrigatoriosValidos: this.instrumentoCamposAdicionaisObrigatoriosValidos
    });
  }

  onInstrumentoTurmaChange(): void {
    this.instrumentoForm.itemEducacionalId = null;
    this.instrumentoForm.periodoAvaliativoId = null;
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.instrumentoItensDisponiveis = [];
    this.instrumentoPeriodos = [];
    this.instrumentoContextoRegistros = [];
    this.instrumentoCalendario = null;
    this.instrumentoConfiguracaoEstabelecimento = null;
    this.instrumentoConfiguracaoTurma = null;
    this.instrumentoEncerramentosTurma = [];

    if (!this.instrumentoForm.turmaId) {
      return;
    }

    this.carregarDependenciasTurmaInstrumento();
  }

  onInstrumentoItemEducacionalChange(): void {
    this.instrumentoForm.periodoAvaliativoId = null;
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.instrumentoPeriodos = [];
    this.instrumentoContextoRegistros = [];

    if (!this.instrumentoForm.itemEducacionalId) {
      return;
    }

    Promise.all([
      this.loadInstrumentoPeriodos(),
      this.loadInstrumentosContexto()
    ]).catch(() => undefined);
  }

  onInstrumentoPeriodoChange(): void {
    this.instrumentoForm.instrumentoRecuperacaoId = null;
    this.loadInstrumentosContexto().catch(() => undefined);
  }

  onInstrumentoTipoChange(): void {
    this.instrumentoForm.instrumentoRecuperacaoId = null;

    if (this.instrumentoForm.tipoInstrumento === 'RECUPERACAO_PARALELA') {
      this.instrumentoForm.notaMinima = '';
      this.instrumentoForm.notaMaxima = '';
    }

  }

  onInstrumentoRecuperacaoChange(): void {
    const instrumentoRecuperacao = this.instrumentosRecuperacaoOptions.find((item) => item.id === this.instrumentoForm.instrumentoRecuperacaoId);
    if (!instrumentoRecuperacao) {
      this.instrumentoForm.modoAvaliacao = this.getModoAvaliacaoInstrumentoAtual();
      return;
    }
    this.preloadInstrumentoRecuperacaoDetalhes(instrumentoRecuperacao.id);

    const raw = instrumentoRecuperacao.raw;
    this.instrumentoForm.conteudo = String(raw['conteudo'] || this.instrumentoForm.conteudo || '');
    this.instrumentoForm.criteriosAvaliativos = String(raw['criteriosAvaliativos'] || this.instrumentoForm.criteriosAvaliativos || '');
    this.instrumentoForm.notaMinima = raw['notaMinima'] === null || raw['notaMinima'] === undefined ? '' : String(raw['notaMinima']);
    this.instrumentoForm.notaMaxima = raw['notaMaxima'] === null || raw['notaMaxima'] === undefined ? '' : String(raw['notaMaxima']);
    this.instrumentoForm.modoAvaliacao = instrumentoRecuperacao.modoAvaliacao;
  }

  /**
   * Carrega as dependências do modal na ordem necessária para manter o formulário consistente.
   */
  private async carregarDadosFormularioInstrumento(registro?: InstrumentoAvaliacaoRegistro): Promise<void> {
    await this.loadInstrumentoTurmas();

    if (registro?.turma?.id) {
      this.instrumentoForm.turmaId = registro.turma.id;
    }

    if (!this.instrumentoForm.turmaId && this.instrumentoTurmasDisponiveis.length) {
      this.instrumentoForm.turmaId = this.instrumentoTurmasDisponiveis[0].id;
    }

    await this.carregarDependenciasTurmaInstrumento();
  }

  private async carregarDependenciasTurmaInstrumento(): Promise<void> {
    await Promise.all([
      this.loadInstrumentoItensTurma(),
      this.loadInstrumentoConfiguracoes(),
      this.loadInstrumentoCalendario(),
      this.loadInstrumentoEncerramentos()
    ]);

    await Promise.all([
      this.loadInstrumentoPeriodos(),
      this.loadInstrumentosContexto()
    ]);
  }

  private loadInstrumentoTurmas(): Promise<void> {
    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getTurmasContexto()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const turmasDisponiveis = (response?.content || []).map((turma) => ({
              ...turma,
              descricaoExibicao: turma.descricao || turma.nome || `Turma ${turma.id}`
            }));

            if (this.instrumentoTurmaBloqueadaContexto && this.turma?.id) {
              const turmaContexto = turmasDisponiveis.find((turma) => turma.id === this.turma?.id);
              this.instrumentoTurmasDisponiveis = turmaContexto
                ? [turmaContexto]
                : [{ ...this.turma, descricaoExibicao: this.turma.descricao || `Turma ${this.turma.id}` }];
            } else {
              this.instrumentoTurmasDisponiveis = turmasDisponiveis;
            }

            resolve();
          },
          error: () => {
            this.instrumentoTurmasDisponiveis = this.turma ? [{ ...this.turma, descricaoExibicao: this.turma.descricao || `Turma ${this.turma.id}` }] : [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentoItensTurma(): Promise<void> {
    if (!this.instrumentoForm.turmaId) {
      this.instrumentoItensDisponiveis = [];
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getItensTurma(this.instrumentoForm.turmaId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (itens: InstrumentoItemEducacionalOption[]) => {
            this.instrumentoItensDisponiveis = (itens || []).map((item) => ({
              id: item.id,
              descricao: item.descricao || item.nome || '',
              isCampoExperiencia: !!item.isCampoExperiencia,
              etapaMatrizDisciplina: item.etapaMatrizDisciplina as { id: number } | undefined
            }));

            if (!this.instrumentoItensDisponiveis.some((item) => item.id === this.instrumentoForm.itemEducacionalId)) {
              this.instrumentoForm.itemEducacionalId = this.instrumentoItensDisponiveis[0]?.id || null;
            }
            resolve();
          },
          error: () => {
            this.instrumentoItensDisponiveis = [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentoConfiguracoes(): Promise<void> {
    if (!this.instrumentoForm.turmaId) {
      this.instrumentoConfiguracaoEstabelecimento = null;
      this.instrumentoConfiguracaoTurma = null;
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      combineLatest([
        this.instrumentoAvaliacaoService.getConfiguracaoEstabelecimento(this.instrumentoForm.turmaId!),
        this.avaliacaoService.getConfiguracaoAvaliacao(this.instrumentoForm.turmaId!)
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ([configEstabelecimento, configTurma]) => {
            this.instrumentoConfiguracaoEstabelecimento = configEstabelecimento || null;
            this.instrumentoConfiguracaoTurma = (configTurma || null) as Record<string, unknown> | null;
            resolve();
          },
          error: () => {
            this.instrumentoConfiguracaoEstabelecimento = null;
            this.instrumentoConfiguracaoTurma = null;
            resolve();
          }
        });
    });
  }

  private loadInstrumentoCalendario(): Promise<void> {
    const estabelecimentoId = this.instrumentoTurmaSelecionada?.estabelecimento?.id;
    if (!estabelecimentoId) {
      this.instrumentoCalendario = null;
      return Promise.resolve();
    }

    return this.calendarioService.getCalendario(estabelecimentoId).then((calendario) => {
      this.instrumentoCalendario = calendario;
    });
  }

  private loadInstrumentoEncerramentos(): Promise<void> {
    if (!this.instrumentoForm.turmaId) {
      this.instrumentoEncerramentosTurma = [];
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.avaliacaoService.getEncerramentosByTurma(this.instrumentoForm.turmaId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.instrumentoEncerramentosTurma = Array.isArray(data) ? data : [];
            resolve();
          },
          error: () => {
            this.instrumentoEncerramentosTurma = [];
            resolve();
          }
        });
    });
  }

  /**
   * Busca instrumentos do mesmo contexto para validações, vínculos de recuperação e edição segura.
   */
  private loadInstrumentosContexto(): Promise<void> {
    if (!this.instrumentoForm.turmaId || !this.instrumentoForm.itemEducacionalId) {
      this.instrumentoContextoRegistros = [];
      return Promise.resolve();
    }

    const mesmoContextoTelaAtual = this.instrumentoForm.turmaId === this.turma?.id &&
      this.instrumentoForm.itemEducacionalId === this.itemEducacional?.id &&
      (this.instrumentoTurmaEAtividade || this.instrumentoForm.periodoAvaliativoId === this.periodo?.id);

    // Quando o contexto do modal coincide com o da grade, reaproveita os dados já carregados.
    if (mesmoContextoTelaAtual && this.instrumentosResumo.length) {
      this.instrumentoContextoRegistros = this.instrumentosResumo.map((item) => this.toInstrumentoRegistro(item));
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.listPorContexto({
        turmaId: this.instrumentoForm.turmaId!,
        periodoAvaliativoId: !this.instrumentoTurmaEAtividade ? this.instrumentoForm.periodoAvaliativoId : null,
        itemEducacionalId: this.instrumentoForm.itemEducacionalId
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.hydrateInstrumentosComPublicacao(response?.content || [])
              .then((registros) => {
                this.instrumentoContextoRegistros = registros;
                resolve();
              });
          },
          error: () => {
            this.instrumentoContextoRegistros = [];
            resolve();
          }
        });
    });
  }

  private loadInstrumentosTelaAtual(forceReload = false): Promise<void> {
    if (!this.turma?.id || !this.itemEducacional?.id) {
      this.instrumentosTelaRegistros = [];
      return Promise.resolve();
    }

    if (!this.isTurmaAtividade && !this.periodo?.id) {
      this.instrumentosTelaRegistros = [];
      return Promise.resolve();
    }

    if (!forceReload && this.instrumentosResumo.length) {
      this.instrumentosTelaRegistros = this.instrumentosResumo.map((item) => this.toInstrumentoRegistro(item));
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.listPorContexto({
        turmaId: this.turma!.id,
        periodoAvaliativoId: !this.isTurmaAtividade ? this.periodo?.id : null,
        itemEducacionalId: this.itemEducacional!.id
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.hydrateInstrumentosComPublicacao(response?.content || [])
              .then((registros) => {
                this.instrumentosTelaRegistros = registros;
                resolve();
              });
          },
          error: () => {
            this.instrumentosTelaRegistros = [];
            resolve();
          }
        });
    });
  }

  private salvarInstrumentoComRecuperacaoVinculada(
    payload: InstrumentoAvaliacaoRegistro,
    recuperacaoVinculada: InstrumentoResumoView,
    adicionarNovo: boolean
  ): void {
    this.instrumentoAvaliacaoService.get(recuperacaoVinculada.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (recuperacaoRegistro) => {
          this.instrumentoSalvando = false;
          this.instrumentosFacade.abrirRecuperacaoVinculada(
            payload,
            this.buildRecuperacaoVinculadaEditable(recuperacaoRegistro, payload),
            adicionarNovo
          );
        },
        error: (error) => {
          this.instrumentoSalvando = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  fecharRecuperacaoVinculada(): void {
    this.instrumentosFacade.fecharRecuperacaoVinculada();
  }

  confirmarRecuperacaoVinculada(): void {
    const erros = this.instrumentoRecuperacaoVinculadaErros;
    if (erros.length) {
      this.notificationService.warning({
        title: 'Revise os dados da recuperação paralela',
        message: erros[0]
      });
      return;
    }

    const instrumentoBase = this.instrumentoRecuperacaoVinculada.instrumentoBase;
    const instrumentoRecuperacao = this.instrumentoRecuperacaoVinculada.instrumentoRecuperacao;
    if (!instrumentoBase || !instrumentoRecuperacao?.id) {
      return;
    }

    this.instrumentoRecuperacaoVinculada.salvando = true;
    this.instrumentoSalvando = true;

    this.instrumentoAvaliacaoService.save(instrumentoBase)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (instrumentoSalvo) => {
          const payloadRecuperacao = this.buildRecuperacaoVinculadaPayload(instrumentoRecuperacao!, instrumentoSalvo);
          this.instrumentoAvaliacaoService.updatePeriodoOrDate(payloadRecuperacao)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                const adicionarNovo = this.instrumentoRecuperacaoVinculada.adicionarNovo;
                this.fecharRecuperacaoVinculada();
                this.notificationService.info({
                  title: 'Recuperação paralela ajustada',
                  message: 'A recuperação paralela vinculada foi atualizada conforme a nova data/período do instrumento.'
                });
                this.finalizarSucessoInstrumento(adicionarNovo);
              },
              error: (error) => {
                this.instrumentoRecuperacaoVinculada.salvando = false;
                this.instrumentoSalvando = false;
                this.errorHandler.handleError(error);
              }
            });
        },
        error: (error) => {
          this.instrumentoRecuperacaoVinculada.salvando = false;
          this.instrumentoSalvando = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  private finalizarSucessoInstrumento(adicionarNovo: boolean): void {
    this.instrumentoSalvando = false;
    this.instrumentoRegistroOriginal = null;
    this.notificationService.success({
      title: 'Instrumento salvo',
      message: 'O instrumento de avaliação foi salvo com sucesso.'
    });
    this.syncContextFromInstrumentoForm();
    this.atualizaListaAvaliacao();

    if (adicionarNovo) {
      this.instrumentoForm = createInstrumentoFormulario({
        turmaId: this.turma?.id || null,
        itemEducacionalId: this.itemEducacional?.id || null,
        periodoAvaliativoId: this.periodo?.id || null
      });
      this.instrumentoArquivos = [];
      this.resetInstrumentoCampoAdicional();
      this.syncInstrumentoFormWithContext();
      this.carregarDadosFormularioInstrumento().finally(() => {
        this.instrumentoSnapshotInicial = this.serializeInstrumentoForm();
        this.instrumentoArquivosSnapshotInicial = this.serializeInstrumentoArquivos();
        this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
        this.instrumentoMostrarErros = false;
      });
      return;
    }

    this.fecharFormularioInstrumento();
  }

  private cloneInstrumentoCampoAdicional<T>(value: T): T {
    return cloneInstrumentoCampoAdicional(value);
  }

  private hasInstrumentoCampoAdicionalConteudo(value: InstrumentoCampoAdicionalConfig): boolean {
    if (!value) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  private carregarCampoAdicionalInstrumento(force = false): Promise<void> {
    const instrumentoId = this.instrumentoForm.id || this.instrumentoRegistroOriginal?.id || null;

    // Sem ID = novo instrumento: busca o template de campos adicionais
    if (!instrumentoId) {
      if (!force && this.instrumentoCamposAdicionaisTemplateCarregado) {
        return Promise.resolve();
      }

      this.instrumentoCamposAdicionaisCarregando = true;
      return new Promise((resolve) => {
        this.instrumentoAvaliacaoService.getCamposAdicionaisTemplate()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (campoAdicional) => {
              this.instrumentoCampoAdicional = this.cloneInstrumentoCampoAdicional(campoAdicional as InstrumentoCampoAdicionalConfig);
              this.instrumentoCamposAdicionaisTemplateCarregado = true;
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCamposAdicionaisCarregando = false;
              resolve();
            },
            error: () => {
              this.instrumentoCampoAdicional = null;
              this.instrumentoCamposAdicionaisTemplateCarregado = false;
              this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
              this.instrumentoCamposAdicionaisCarregando = false;
              resolve();
            }
          });
      });
    }

    if (!force && this.instrumentoCamposAdicionaisCarregadoParaId === instrumentoId) {
      return Promise.resolve();
    }

    this.instrumentoCamposAdicionaisCarregando = true;

    return new Promise((resolve) => {
      this.instrumentoAvaliacaoService.getCampoAdicional(instrumentoId!)
        .pipe(takeUntil(this.closeInstrumentoFormulario$))
        .subscribe({
          next: (campoAdicional) => {
            const possuiCampoAdicionalAtual = this.hasInstrumentoCampoAdicionalConteudo(this.instrumentoCampoAdicional);
            const campoAdicionalResposta = this.cloneInstrumentoCampoAdicional(campoAdicional as InstrumentoCampoAdicionalConfig);
            const possuiCampoAdicionalResposta = this.hasInstrumentoCampoAdicionalConteudo(campoAdicionalResposta);

            if (possuiCampoAdicionalResposta || !possuiCampoAdicionalAtual) {
              this.instrumentoCampoAdicional = campoAdicionalResposta;
            }
            this.instrumentoCamposAdicionaisCarregadoParaId = instrumentoId;
            this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
            this.instrumentoCamposAdicionaisCarregando = false;
            resolve();
          },
          error: () => {
            if (!this.hasInstrumentoCampoAdicionalConteudo(this.instrumentoCampoAdicional)) {
              this.instrumentoCampoAdicional = null;
            }
            this.instrumentoCamposAdicionaisCarregadoParaId = null;
            this.instrumentoCampoAdicionalSnapshotInicial = this.serializeInstrumentoCampoAdicional();
            this.instrumentoCamposAdicionaisCarregando = false;
            resolve();
          }
        });
    });
  }

  private syncContextFromInstrumentoForm(): void {
    const itemEducacionalId = this.instrumentoForm.itemEducacionalId;
    if (itemEducacionalId) {
      const itemSelecionado = this.itensEducacionais.find((item) => item.id === itemEducacionalId)
        || this.instrumentoItensDisponiveis.find((item) => item.id === itemEducacionalId)
        || null;

      if (itemSelecionado) {
        this.itemEducacional = itemSelecionado as ItemEducacional;
        this.updateNavigationButtons();
      }
    }

    if (!this.isTurmaAtividade && this.instrumentoForm.periodoAvaliativoId) {
      let periodoSelecionado = this.periodos.find((periodo) => periodo.id === this.instrumentoForm.periodoAvaliativoId) || null;
      const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);

      if (!periodoSelecionado && periodoDetalhado) {
        periodoSelecionado = {
          id: Number(periodoDetalhado.periodoAvaliativo?.id || periodoDetalhado.id || 0),
          descricao: String(periodoDetalhado.periodoAvaliativo?.descricao || periodoDetalhado.descricao || ''),
          dataInicial: String(periodoDetalhado.periodoAvaliativo?.dataInicial || periodoDetalhado.dataInicial || ''),
          dataFinal: String(periodoDetalhado.periodoAvaliativo?.dataFinal || periodoDetalhado.dataFinal || '')
        };
      }

      if (periodoSelecionado) {
        this.periodo = periodoSelecionado;
      }
    }
  }

  private instrumentoPossuiNotasInformadas(instrumentoId?: number | null): boolean {
    if (!instrumentoId) {
      return false;
    }

    return this.linhas.some((linha) =>
      (linha.notas || []).some((nota) => {
        if (Number(nota.avaliavel?.id || 0) !== instrumentoId) {
          return false;
        }

        const temValorNumerico = nota.notaNumerica !== null && nota.notaNumerica !== undefined;
        const temValorDescritivo = typeof nota.notaDescritiva === 'string' && nota.notaDescritiva.trim() !== '';
        const temConceito = !!nota.notaConceito;
        const temValorDigitado = nota.$$nota !== null && nota.$$nota !== undefined && String(nota.$$nota).trim() !== '';

        const notaCompat = nota as NotaViewCompat;
        const temParecer = !!(notaCompat.parecerDetalhadoConteudo || notaCompat.parecer);

        return temValorNumerico || temValorDescritivo || temConceito || temValorDigitado || temParecer;
      })
    );
  }

  private getRecuperacaoParalelaVinculada(instrumentoId: number | null): InstrumentoResumoView | undefined {
    if (!instrumentoId) {
      return undefined;
    }

    return this.instrumentoContextoResumo.find((item) =>
      item.tipoInstrumento === 'RECUPERACAO_PARALELA' && item.instrumentoRecuperacaoId === instrumentoId
    ) || this.instrumentosResumo.find((item) =>
      item.tipoInstrumento === 'RECUPERACAO_PARALELA' && item.instrumentoRecuperacaoId === instrumentoId
    );
  }

  private isPeriodoEncerradoComLista(periodo: Periodo | null, encerramentos: Array<Record<string, unknown>>): boolean {
    if (!periodo || !encerramentos.length) {
      return false;
    }

    return encerramentos.some((enc) => this.matchesEncerramento(
      enc as { tipoPeriodo?: string; identificadorExameFinal?: string; status?: string; periodoId?: number },
      periodo
    ));
  }

  private isPeriodoLetivoEncerrado(encerramentos: Array<Record<string, unknown>>): boolean {
    return isPeriodoLetivoEncerrado(encerramentos);
  }

  private toInstrumentoRegistro(item: InstrumentoResumoView): InstrumentoAvaliacaoRegistro {
    return toInstrumentoRegistro(item);
  }

  private mergeInstrumentoWithResumo(registro: InstrumentoAvaliacaoRegistro): InstrumentoAvaliacaoRegistro {
    const instrumentoId = Number(registro.id || 0);
    const resumo = instrumentoId ? this.instrumentosResumo.find((item) => item.id === instrumentoId) : undefined;
    return mergeInstrumentoComResumo(registro, resumo);
  }

  private hydrateInstrumentosComPublicacao(registros: InstrumentoAvaliacaoRegistro[]): Promise<InstrumentoAvaliacaoRegistro[]> {
    const registrosMesclados = (registros || []).map((registro) => this.mergeInstrumentoWithResumo(registro));
    const pendentes = registrosMesclados.filter((registro) => Number(registro.id || 0) > 0 && !this.hasInstrumentoPublicacaoState(registro));

    if (!pendentes.length) {
      return Promise.resolve(registrosMesclados);
    }

    return new Promise((resolve) => {
      forkJoin(
        pendentes.map((registro) =>
          this.instrumentoAvaliacaoService.get(Number(registro.id || 0)).pipe(
            takeUntil(this.destroy$),
            catchError(() => of(registro))
          )
        )
      ).subscribe({
        next: (registrosDetalhados) => {
          const detalhesPorId = new Map<number, InstrumentoAvaliacaoRegistro>();

          registrosDetalhados.forEach((registroDetalhado) => {
            const id = Number(registroDetalhado.id || 0);
            if (id > 0) {
              detalhesPorId.set(id, this.mergeInstrumentoWithResumo(registroDetalhado));
            }
          });

          resolve(
            registrosMesclados.map((registro) => detalhesPorId.get(Number(registro.id || 0)) || registro)
          );
        },
        error: () => resolve(registrosMesclados)
      });
    });
  }

  private atualizarEstadoPublicacaoInstrumento(instrumentoId: number, publicado: boolean, dataPublicacao: string | null): void {
    const atualizarRegistro = (registro: InstrumentoAvaliacaoRegistro): InstrumentoAvaliacaoRegistro => {
      if (Number(registro.id || 0) !== instrumentoId) {
        return registro;
      }

      return {
        ...registro,
        publicavel: {
          ...(registro.publicavel || {}),
          publicado,
          dataPublicacao
        },
        publicado,
        publicar: publicado,
        dataPublicacao
      };
    };

    this.instrumentosTelaRegistros = this.instrumentosTelaRegistros.map(atualizarRegistro);
    this.instrumentoContextoRegistros = this.instrumentoContextoRegistros.map(atualizarRegistro);
  }

  private getModoAvaliacaoInstrumentoAtual(): string | undefined {
    if (this.instrumentoForm.instrumentoRecuperacaoId) {
      return this.instrumentosRecuperacaoOptions.find((item) => item.id === this.instrumentoForm.instrumentoRecuperacaoId)?.modoAvaliacao;
    }

    if (this.isTurmaAtividade) {
      return this.instrumentoPeriodos[0]?.modoAvaliacao || this.instrumentoForm.modoAvaliacao;
    }

    return this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId)?.modoAvaliacao || this.instrumentoForm.modoAvaliacao;
  }

  protected sanitizeInstrumentoCampoNumerico(value: unknown, campo?: 'notaMinima' | 'notaMaxima' | 'peso'): string {
    return sanitizeInstrumentoCampoNumerico(value, campo, this.instrumentoPesoMaximoLimite, this.instrumentoConfiguracaoTurma);
  }

  listFormulasCalculo(params: { term: string }): void {
    const filter = params.term ? `(descricao like "${params.term}") and situacaoCadastral = "ATIVO"` : 'situacaoCadastral = "ATIVO"';
    
    this.avaliacaoService.getFormulasCalculo(filter, 0, 999)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.formulasCalculo = data.content;
        },
        error: (error) => {
          this.formulasCalculo = [];
        }
      });
  }

  formatResultFormulaCalculo(formulaCalculo: FormulaCalculo | null): string {
    return formatResultFormulaCalculo(formulaCalculo);
  }

  onFormulaChange(): void {
    if (this.formulaCalculo && this.calculoFormula) {
      this.calculoFormula.formulaCalculo = this.formulaCalculo;
    }
  }

  prevItensEducacionais(): void {
    (document.activeElement as HTMLElement)?.blur();
    const index = this.findItemEducacionalIndex() - 1;
    if (index >= 0) {
      this.changeItemEducacional(this.itensEducacionais[index]);
    }
  }

  nextItensEducacionais(): void {
    (document.activeElement as HTMLElement)?.blur();
    const index = this.findItemEducacionalIndex() + 1;
    if (index < this.itensEducacionais.length) {
      this.changeItemEducacional(this.itensEducacionais[index]);
    }
  }

  changeItemEducacional(item: ItemEducacional): void {
    this.itemEducacional = item;
    this.linhas = [];
    this.instrumentoVisualizadoId = null;
    this.updateNavigationButtons();
    this.atualizaListaAvaliacao();
  }

  calcularFormula(): void {
    if (!this.turma || !this.itemEducacional || !this.formulaCalculo) return;

    this.calculoTracker = true;
    this.avaliacaoService.calcularFormula(
      this.turma.id,
      this.itemEducacional.id,
      this.formulaCalculo.id,
      this.periodo?.id
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.monitorarRetornoCalculo(),
        error: (error) => {
          this.calculoTracker = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  private loadStatusCalculo(): Promise<void> {
    if (!this.turma || !this.itemEducacional || !this.periodo) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.avaliacaoService.statusCalculo(this.turma!.id, this.itemEducacional!.id, this.periodo!.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (dados) => {
            this.calculoFormula = dados.calculo || null;
            this.formulaCalculo = this.calculoFormula?.formulaCalculo || null;
            resolve();
          },
          error: (error) => {
            // Se não houver cálculo, não é erro
            this.calculoFormula = null;
            this.formulaCalculo = null;
            resolve();
          }
        });
    });
  }

  monitorarRetornoCalculo(): void {
    if (!this.turma || !this.itemEducacional || !this.periodo) return;

    this.avaliacaoService.statusCalculo(this.turma.id, this.itemEducacional.id, this.periodo.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dados) => {
          this.calculoFormula = dados.calculo;
          if (this.calculoFormula?.status !== 'PROCESSANDO') {
            this.atualizaListaAvaliacao();
          } else {
            setTimeout(() => this.monitorarRetornoCalculo(), 20000);
          }
        },
        error: (error) => {
          this.calculoTracker = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  desfazCalculo(): void {
    if (!this.turma || !this.periodo || !this.itemEducacional) return;

    this.calculoTracker = true;
    this.avaliacaoService.removeCalculoInstrumento(this.turma.id, this.periodo.id, this.itemEducacional.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.atualizaListaAvaliacao(),
        error: (error) => {
          this.calculoTracker = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  /**
   * Orquestra o carregamento da grade após garantir alunos e encerramentos da turma.
   */
  private loadRegistrosAvaliacoes(): void {
    if (this.isStandaloneInstrumentosPage) {
      this.colunas = [];
      this.linhas = [];
      this.colunaMediaPeriodo = null;
      this.loadingTracker = false;
      this.isLoadingRegistros = false;
      return;
    }

    if (this.isLoadingRegistros) {
      return;
    }
    
    if (!this.turma || !this.itemEducacional) {
      if (this.porInstrumento && this.turma) {
        this.loadEnturmacoes();
      }
      return;
    }

    this.resetLoadingState();

    Promise.all([
      this.loadEnturmacoes(),
      this.loadEncerramentos()
    ]).then(() => {
      if (!this.hasEnturmacoes()) {
        this.finishLoading();
        return;
      }

      if (this.porInstrumento) {
        this.loadRegistrosPorInstrumento();
      } else {
        this.loadRegistrosPorAno();
      }
    });
  }

  private resetLoadingState(): void {
    this.isLoadingRegistros = true;
    this.loadingTracker = true;
    this.calculoFormula = null;
    this.colunas = [];
    this.linhas = [];
    this.hasAvaliacao = false;
  }

  private hasEnturmacoes(): boolean {
    return this.enturmacoes && this.enturmacoes.length > 0;
  }

  private finishLoading(): void {
    this.loadingTracker = false;
    this.isLoadingRegistros = false;
  }

  /**
   * Carrega a visão detalhada por instrumento, base da grade principal da página.
   */
  private loadRegistrosPorInstrumento(): void {
    const periodoId = this.getPeriodoIdForInstrumento();
    
    if (!periodoId && this.labelItensEducacionais === 'disciplina') {
      this.finishLoading();
      return;
    }

    this.desempenhoData.loadAvaliacoesPorInstrumento(
      this.turma!.id,
      periodoId,
      this.itemEducacional!.id,
      this.destroy$
    )
      .pipe(
        catchError(() => of({ itensAvaliaveis: [], registrosAvaliacao: [], registros: [] }))
      )
      .subscribe({
        next: (avaliacoes) => this.handleAvaliacoesPorInstrumento(avaliacoes, periodoId),
        error: () => this.handleLoadError()
      });
  }

  private getPeriodoIdForInstrumento(): number | undefined {
    return this.labelItensEducacionais === 'atividade' 
      ? undefined 
      : (this.periodo?.id || undefined);
  }

  private handleAvaliacoesPorInstrumento(
    avaliacoes: { itensAvaliaveis?: ItemAvaliavel[]; registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[] },
    periodoId: number | undefined
  ): void {
    const itensAvaliaveisFiltrados = this.desempenhoData.filterItensAvaliaveisPorInstrumento(avaliacoes?.itensAvaliaveis || []);
    const registrosAvaliacao = avaliacoes?.registrosAvaliacao || avaliacoes?.registros || [];
    
    const avaliacoesCompletas = {
      registros: registrosAvaliacao,
      itensAvaliaveis: itensAvaliaveisFiltrados
    };

    if (!avaliacoesCompletas) {
      this.handleLoadError();
      return;
    }

    if (this.itemEducacional && periodoId) {
      this.loadStatusCalculo().then(() => {
        this.createViewFromAvaliacoes(avaliacoesCompletas);
      });
    } else {
      this.createViewFromAvaliacoes(avaliacoesCompletas);
    }
  }

  private createViewFromAvaliacoes(avaliacoesCompletas: { registros: RegistroAvaliacao[]; itensAvaliaveis: ItemAvaliavel[] }): void {
    const registrosFormatados = {
      avaliacoes: {
        itensAvaliaveis: avaliacoesCompletas.itensAvaliaveis,
        registrosAvaliacao: avaliacoesCompletas.registros,
        registros: avaliacoesCompletas.registros,
        calculo: this.calculoFormula || null
      },
      encerramentos: this.encerramentosTurma
    };
    
    this.createView(registrosFormatados);
    this.finishLoading();
  }

  /**
   * Carrega a visão anual consolidada, agrupando registros por períodos avaliativos.
   */
  private loadRegistrosPorAno(): void {
    this.semDesempenhos = false;
    this.desempenhoData.loadAvaliacoesCompletos(this.turma!.id, this.itemEducacional!.id, this.destroy$)
      .subscribe({
        next: (resultado) => this.handleAvaliacoesCompletos(resultado),
        error: () => this.handleLoadError()
      });
  }

  private handleAvaliacoesCompletos(resultado: { content?: RegistroAvaliacao[] }): void {
    // Registros vazios NÃO significam "nada a exibir": a estrutura da grade (colunas/períodos)
    // vem dos itensAvaliaveis. Seguindo o mesmo critério da web, sempre carregamos os itens
    // e construímos a grade; o empty-state só é ativado quando itensAvaliaveis é vazio.
    const avaliacoes = resultado.content || [];

    this.desempenhoData.loadItensAvaliaveisForYearView(this.turma!.id, this.itemEducacional!.id, this.destroy$)
      .subscribe({
        next: (itensResult) => this.handleItensAvaliaveis(itensResult, avaliacoes),
        error: () => this.finishLoading()
      });
  }

  private handleItensAvaliaveis(
    itensResult: { content?: ItemAvaliavel[] },
    avaliacoes: RegistroAvaliacao[]
  ): void {
    const itensAvaliaveis = itensResult.content || [];

    // Sem itensAvaliaveis não há configuração de avaliação para este componente:
    // equivale ao `if (_.isEmpty(vm.itensAvaliaveis)) { return; }` da web.
    if (!itensAvaliaveis.length) {
      this.linhas = [];
      this.colunas = [];
      this.semDesempenhos = true;
      this.finishLoading();
      return;
    }

    this.desempenhoData.loadPeriodos(this.turma!.id, this.destroy$)
      .subscribe({
        next: (periodosResult) => this.createYearViewFromData(itensAvaliaveis, avaliacoes, periodosResult),
        error: () => this.finishLoading()
      });
  }

  private createYearViewFromData(
    itensAvaliaveis: ItemAvaliavel[],
    avaliacoes: RegistroAvaliacao[],
    periodosResult: Periodo[]
  ): void {
    // Itens sem modoAvaliacao ou com 'NAO_OFERECE' não geram colunas (mesmo critério da web,
    // desempenho.controller.js). Inclui EXAME_FINAL não oferecido para o item educacional.
    const itensAvaliaveisParaView = itensAvaliaveis.filter(item =>
      item.modoAvaliacao != null && item.modoAvaliacao !== 'NAO_OFERECE'
    );

    const registrosFormatados = {
      avaliacoes: {
        itensAvaliaveis: itensAvaliaveis,
        registrosAvaliacao: avaliacoes,
        registros: avaliacoes,
        calculo: null
      },
      abonos: []
    };
    
    const yearView = this.notaService.createYearView(
      {
        ...registrosFormatados,
        avaliacoes: {
          ...registrosFormatados.avaliacoes,
          calculo: registrosFormatados.avaliacoes.calculo || undefined
        }
      },
      this.itemEducacional!,
      itensAvaliaveisParaView,
      periodosResult,
      this.enturmacoes
    );
    
    this.colunas = yearView.colunas;
    this.linhas = yearView.linhas;
    this.colunaMediaPeriodo = yearView.colunaMediaFinal || null;
    this.hasAvaliacao = yearView.colunas.length > 0;
    
    this.finishLoading();
  }

  private handleLoadError(): void {
    this.colunas = [];
    this.linhas = [];
    this.hasAvaliacao = false;
    this.semDesempenhos = false;
    this.finishLoading();
  }

  semDesempenhos = false;

  private enturmacoes: any[] = [];
  private encerramentosTurma: any[] = [];

  private loadEnturmacoes(): Promise<any[]> {
    if (!this.turma || !this.turma.id) {
      return Promise.resolve([]);
    }

    // Construir filtro de busca (enviado ao backend, igual ao padrão da versão web)
    const partesFiltro: string[] = [];
    if (this.searchFilter.keywords) {
      partesFiltro.push(`nome like "${this.searchFilter.keywords}"`);
    }
    if (this.filtroSituacaoEnturmacao) {
      partesFiltro.push(`situacaoMatricula in ("${this.filtroSituacaoEnturmacao}")`);
    }
    const filter = partesFiltro.join(' AND ');

    return new Promise((resolve, reject) => {
      this.avaliacaoService.getEnturmacoesHistoricas(this.turma!.id, filter, 0, 999)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            const raw = Array.isArray(data) ? data : [];
            // Normaliza campos potencialmente nulos vindos da API para evitar crashes
            // em templates e serviços que esperam strings
            this.enturmacoes = raw.map((e: any) => ({
              ...e,
              situacao: e.situacao ?? null,
              numeroChamada: e.numeroChamada ?? 0,
              matricula: e.matricula ? {
                ...e.matricula,
                nome: e.matricula.nome ?? '',
                nomeSocial: e.matricula.nomeSocial ?? e.matricula.nomeFantasia ?? null,
                situacao: e.matricula.situacao ?? null,
              } : { id: 0, nome: '', situacao: null },
            }));
            resolve(this.enturmacoes);
          },
          error: (error) => {
            this.enturmacoes = [];
            resolve([]);
          }
        });
    });
  }

  private loadEncerramentos(): Promise<any[]> {
    if (!this.turma) {
      return Promise.resolve([]);
    }

    return new Promise((resolve, reject) => {
      this.avaliacaoService.getEncerramentosByTurma(this.turma!.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.encerramentosTurma = data;
            resolve(data);
          },
          error: (error) => {
            resolve([]);
          }
        });
    });
  }

  /**
   * Transforma os dados carregados em colunas, linhas e indicadores usados no template.
   */
  private createView(registros: any): void {
    if (!this.itemEducacional) return;
    const view = this.notaService.createView(
      registros,
      this.itemEducacional,
      this.periodo,
      this.enturmacoes,
      this.encerramentosTurma,
      this.porInstrumento
    );

    this.colunas = view.colunas || [];
    this.linhas = view.linhas || [];
    this.colunaMediaPeriodo = view.colunaMediaPeriodo || null;
    this.calculoFormula = view.calculoFormula ? {
      ...view.calculoFormula,
      id: view.calculoFormula.id || 0
    } as CalculoFormula : null;
    this.formulaCalculo = this.calculoFormula?.formulaCalculo || null;

    this.hasAvaliacao = registros.avaliacoes?.itensAvaliaveis?.length > 0;

    if (this.calculoFormula && this.calculoFormula.status === 'PROCESSANDO') {
      this.monitorarRetornoCalculo();
    }
  }

  hasAvaliacao = false;

  private atualizaListaAvaliacao(): void {
    this.calculoTracker = false;
    this.loadInstrumentosTelaAtual(true).catch(() => undefined);
    this.loadRegistrosAvaliacoes();
  }

  private findItemEducacionalIndex(): number {
    if (!this.itemEducacional) return -1;
    return this.itensEducacionais.findIndex(item => item.id === this.itemEducacional!.id);
  }

  private updateNavigationButtons(): void {
    const index = this.findItemEducacionalIndex();
    this.disabledPrev = index <= 0;
    this.disabledNext = index >= this.itensEducacionais.length - 1;
  }

  // Getters mantêm o template declarativo e concentram a regra de exibição da tela.
  get listaEnturmacoes(): LinhaDesempenho[] {
    if (!this.linhas || this.linhas.length === 0) {
      return [];
    }

    const itemEducacionalId = this.shouldFilterByItemEducacional() ? this.itemEducacional?.id || null : null;
    return this.desempenhoFilters.applyFiltersToLinhas(
      this.linhas,
      this.selectedAluno,
      this.searchFilter.keywords || '',
      itemEducacionalId
    );
  }

  get listaEnturmacoesOrdenada(): LinhaDesempenho[] {
    return [...this.listaEnturmacoes].sort((a, b) => {
      switch (this.ordenacaoAtual) {
        case 'nomeAsc':
          return this.resolveNomeAluno(a).localeCompare(this.resolveNomeAluno(b), 'pt-BR', { sensitivity: 'base' });
        case 'nomeDesc':
          return this.resolveNomeAluno(b).localeCompare(this.resolveNomeAluno(a), 'pt-BR', { sensitivity: 'base' });
        case 'numeroChamadaDesc':
          return this.resolveNumeroChamada(b) - this.resolveNumeroChamada(a);
        case 'numeroChamadaAsc':
        default:
          return this.resolveNumeroChamada(a) - this.resolveNumeroChamada(b);
      }
    });
  }

  get visualizacaoAtualLabel(): string {
    return this.porInstrumento && !this.isTurmaEjaModular
      ? 'Por instrumentos de avaliação'
      : 'Por resultados de avaliações anuais';
  }

  get ordenacaoAtualLabel(): string {
    switch (this.ordenacaoAtual) {
      case 'nomeAsc':
        return 'Ordem alfabética A-Z';
      case 'nomeDesc':
        return 'Ordem alfabética Z-A';
      case 'numeroChamadaDesc':
        return 'Nº da chamada decrescente';
      case 'numeroChamadaAsc':
      default:
        return 'Nº da chamada crescente';
    }
  }

  get ordenacaoAtualIcone(): string {
    switch (this.ordenacaoAtual) {
      case 'nomeAsc':
        return 'arrow-down-a-z';
      case 'nomeDesc':
        return 'arrow-up-z-a';
      case 'numeroChamadaDesc':
        return 'arrow-down-1-0';
      case 'numeroChamadaAsc':
      default:
        return 'arrow-down-0-1';
    }
  }

  get ordenacaoAtualTooltip(): string {
    return `Ordenação atual: ${this.ordenacaoAtualLabel}`;
  }

  get periodoAtualLabel(): string {
    if (!this.periodo) {
      return 'Selecione um período';
    }

    return this.formatPeriodoDescricao(this.periodo);
  }

  get podeCalcularFormula(): boolean {
    return !!(this.porInstrumento && !this.isTurmaEjaModular && this.formulaCalculo && this.turma && this.itemEducacional && !this.calculoTracker);
  }

  get totalAlunosFiltrados(): number {
    return this.listaEnturmacoesOrdenada.length;
  }

  get hasFiltrosAtivos(): boolean {
    return !!(this.searchFilter.keywords || this.selectedAluno || this.filtroSituacaoEnturmacao);
  }

  private resolveNumeroChamada(linha: LinhaDesempenho): number {
    return resolveNumeroChamada(linha);
  }

  private resolveNomeAluno(linha: LinhaDesempenho): string {
    return resolveNomeAluno(linha);
  }

  private resolveSituacaoOrdenacao(linha: LinhaDesempenho): string {
    return String(this.getSituacao(linha) || '');
  }

  private shouldFilterByItemEducacional(): boolean {
    return this.labelItensEducacionais === 'atividade' && !!this.itemEducacional;
  }

  get labelItensEducacionais(): string {
    if (!this.turma) return 'disciplina';
    
    const tipoAtendimento = this.turma.tipoAtendimento;
    return (tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO' || 
            tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR') 
      ? 'atividade' 
      : 'disciplina';
  }

  get hasDisciplina(): boolean {
    return !!(this.itemEducacional?.id);
  }

  get exibeSituacaoPeriodo(): boolean {
    return !!this.periodo;
  }

  get situacaoPeriodoAtualLabel(): string {
    if (!this.periodo) {
      return 'Sem período selecionado';
    }

    return this.isPeriodoEncerrado(this.periodo) ? 'Período encerrado' : 'Período em aberto';
  }

  get instrumentosAvaliacaoParaSelecao(): InstrumentoResumoView[] {
    return this.instrumentosTelaResumo;
  }

  get instrumentoVisualizadoResumo(): InstrumentoResumoView | null {
    return this.instrumentosFacade.obterInstrumentoVisualizadoResumo(this.instrumentosAvaliacaoParaSelecao);
  }

  get indiceInstrumentoVisualizado(): number {
    return this.instrumentosFacade.obterIndiceInstrumentoVisualizado(this.instrumentosAvaliacaoParaSelecao);
  }

  get podeSelecionarInstrumentoAnterior(): boolean {
    return this.indiceInstrumentoVisualizado > 0;
  }

  get podeSelecionarProximoInstrumento(): boolean {
    const indiceAtual = this.indiceInstrumentoVisualizado;
    return indiceAtual >= 0 && indiceAtual < this.instrumentosAvaliacaoParaSelecao.length - 1;
  }

  get colunasInstrumentoExibidas(): ColunaDesempenho[] {
    const instrumentos = this.instrumentosAvaliacaoParaSelecao;
    const instrumentoId = this.instrumentoVisualizadoId || instrumentos[0]?.id || null;

    return (this.colunas || []).filter((coluna) => {
      if (coluna.tipoPeriodo === 'CALCULO_INSTRUMENTOS') {
        return false;
      }

      const raw = coluna.rawValue as ItemAvaliavel & { id?: number };
      return instrumentoId ? raw?.id === instrumentoId : true;
    });
  }

  get linhasInstrumentoExibidas(): LinhaDesempenho[] {
    const colunasFiltradas = this.colunasInstrumentoExibidas;
    if (!colunasFiltradas.length) {
      return this.listaEnturmacoesOrdenada;
    }

    return this.listaEnturmacoesOrdenada.map((linha) => {
      const notasOriginais = Array.isArray(linha.notas) ? linha.notas : [];
      const notasFiltradas = colunasFiltradas.map((coluna) => {
        const colunaId = Number((coluna.rawValue as { id?: number } | undefined)?.id || 0);
        if (!colunaId) {
          return undefined as unknown as NotaView;
        }
        return notasOriginais.find((nota) => Number(nota?.avaliavel?.id || 0) === colunaId) as NotaView;
      });

      return {
        ...linha,
        notas: notasFiltradas
      };
    });
  }

  get hasAnyConfiguracaoCha(): boolean {
    return this.desempenhoConfig.hasAnyConfiguracaoCha(this.configuracaoCha as Record<string, { visivel?: boolean }>);
  }

  get isTurmaEjaModular(): boolean {
    return !!(this.turma?.etapaMatriz?.modular);
  }

  get isTurmaAtividade(): boolean {
    return this.turma?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO' ||
           this.turma?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR';
  }

  get hasItensList(): boolean {
    return this.listaEnturmacoes.length > 0;
  }

  get isSemPeriodoAvaliativo(): boolean {
    if (this.periodos.length === 0) {
      return true;
    }

    if (this.periodo && (!this.periodo.dataInicial || !this.periodo.dataFinal)) {
      return true;
    }
    return false;
  }

  get instrumentosResumo(): InstrumentoResumoView[] {
    return (this.colunas || [])
      .filter((coluna) => coluna.tipoPeriodo !== 'CALCULO_INSTRUMENTOS')
      .map((coluna) => {
        const raw = (coluna.rawValue || {}) as Record<string, unknown>;
        const tipoInstrumento = (raw['tipoInstrumento'] as TipoInstrumentoAvaliacaoId) || 'INSTRUMENTO_AVALIACAO';
        const publicavel = (raw['publicavel'] || {}) as { publicado?: boolean; dataPublicacao?: string | null };
        let publicado = publicavel.publicado;

        if (typeof publicado !== 'boolean') {
          publicado = typeof raw['publicado'] === 'boolean'
            ? raw['publicado'] as boolean
            : typeof raw['publicar'] === 'boolean'
              ? raw['publicar'] as boolean
              : false;
        }

        const turmaId = this.extractTurmaIdFromRaw(raw) || this.turma?.id || null;

        return {
          id: Number(raw['id'] || 0),
          descricao: String(raw['descricao'] || coluna.descricao || ''),
          sigla: String(raw['sigla'] || coluna.sigla || ''),
          turmaId,
          data: String(raw['data'] || coluna.data || ''),
          notaMinima: parseUnknownNumber(raw['notaMinima']),
          notaMaxima: parseUnknownNumber(raw['notaMaxima']),
          tipoInstrumento,
          tipoInstrumentoLabel: this.getTipoInstrumentoLabel(tipoInstrumento),
          modoAvaliacao: typeof raw['modoAvaliacao'] === 'string' ? String(raw['modoAvaliacao']) : undefined,
          instrumentoRecuperacaoId: Number(((raw['instrumentoRecuperacao'] as { id?: number } | undefined)?.id) || 0) || null,
          periodoAvaliativoId: Number(((raw['periodoAvaliativo'] as { id?: number } | undefined)?.id) || 0) || null,
          publico: !!publicado,
          dataPublicacao: publicavel.dataPublicacao || (raw['dataPublicacao'] as string | null | undefined) || null,
          raw
        };
      })
      .filter((item) => item.id > 0);
  }

  protected extractTurmaIdFromRaw(raw: Record<string, unknown>): number | null {
    return extractTurmaIdFromRaw(raw);
  }

  get instrumentoContextoResumo(): InstrumentoResumoView[] {
    return (this.instrumentoContextoRegistros || []).map((registro) => this.toInstrumentoResumo(registro));
  }

  get instrumentosTelaResumo(): InstrumentoResumoView[] {
    return (this.instrumentosTelaRegistros || []).map((registro) => this.toInstrumentoResumo(registro));
  }

  get instrumentoTurmaSelecionada(): InstrumentoTurmaOption | null {
    return this.instrumentoTurmasDisponiveis.find((turma) => turma.id === this.instrumentoForm.turmaId) || null;
  }

  get instrumentoItemSelecionado(): ItemEducacional | null {
    return (this.instrumentoItensDisponiveis.find((item) => item.id === this.instrumentoForm.itemEducacionalId) as ItemEducacional | undefined) || null;
  }

  get instrumentoPeriodoSelecionado(): Periodo | null {
    const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
    const periodoAvaliativo = periodoDetalhado?.periodoAvaliativo;
    if (!periodoDetalhado && !periodoAvaliativo) {
      return null;
    }

    return {
      id: Number(periodoAvaliativo?.id ?? periodoDetalhado?.id ?? 0),
      descricao: String(periodoAvaliativo?.descricao || periodoDetalhado?.descricao || ''),
      dataInicial: String(periodoAvaliativo?.dataInicial || periodoDetalhado?.dataInicial || ''),
      dataFinal: String(periodoAvaliativo?.dataFinal || periodoDetalhado?.dataFinal || '')
    };
  }

  get instrumentoTurmaEAtividade(): boolean {
    return this.instrumentoTurmaSelecionada?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO' ||
      this.instrumentoTurmaSelecionada?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR';
  }

  get instrumentoTurmaEjaModular(): boolean {
    return !!this.instrumentoTurmaSelecionada?.etapaMatriz?.modular;
  }

  get instrumentoTurmaOptions(): PrototypeSelectOption[] {
    return this.instrumentoTurmasDisponiveis.map((turma) => ({
      value: turma.id,
      label: turma.descricaoExibicao
    }));
  }

  get instrumentoItemEducacionalOptions(): PrototypeSelectOption[] {
    return this.instrumentoItensDisponiveis.map((item) => ({
      value: item.id,
      label: item.descricao
    }));
  }

  get instrumentoPeriodoEncerradoSelecionado(): boolean {
    const periodo = this.instrumentoPeriodoSelecionado;
    if (!periodo) {
      return false;
    }

    return this.isPeriodoEncerradoComLista(periodo, this.instrumentoEncerramentosTurma);
  }

  get tiposInstrumentoDisponiveis(): InstrumentoTipoOption[] {
    const options: InstrumentoTipoOption[] = [
      { id: 'INSTRUMENTO_AVALIACAO', label: 'Instrumento de avaliação' }
    ];

    if (this.instrumentoConfiguracaoEstabelecimento?.permiteRecuperacaoParalela === true) {
      options.push({ id: 'RECUPERACAO_PARALELA', label: 'Recuperação paralela' });
    }

    if (!this.instrumentoTurmaEAtividade && !this.instrumentoTurmaEjaModular && this.instrumentoConfiguracaoEstabelecimento?.permiteRecuperacaoPeriodoAvaliativo === true) {
      options.push({ id: 'RECUPERACAO_PERIODO_AVALIATIVO', label: 'Recuperação do período avaliativo' });
    }

    if (this.instrumentoConfiguracaoEstabelecimento?.permiteNotaComplementar === true) {
      options.push({ id: 'PONTO_EXTRA', label: 'Nota Complementar/Ponto Extra' });
    }

    return options;
  }

  get instrumentoTipoOptions(): PrototypeSelectOption[] {
    return this.tiposInstrumentoDisponiveis.map((tipo) => ({
      value: tipo.id,
      label: tipo.label
    }));
  }

  get instrumentoPeriodoOptions(): PeriodoSelectOption[] {
    return this.instrumentoPeriodos.map((periodo) => {
      const periodoAvaliativo = periodo.periodoAvaliativo;
      const id = Number(periodoAvaliativo?.id ?? periodo.id ?? 0);
      const descricao = String(periodoAvaliativo?.descricao || periodo.descricao || 'Período avaliativo');
      const dataInicial = String(periodoAvaliativo?.dataInicial || periodo.dataInicial || '');
      const dataFinal = String(periodoAvaliativo?.dataFinal || periodo.dataFinal || '');

      return {
        id,
        descricao,
        dataInicial,
        dataFinal,
        encerrado: this.isPeriodoEncerradoComLista({ id, descricao, dataInicial, dataFinal }, this.instrumentoEncerramentosTurma)
      };
    });
  }

  get periodoOptions(): PeriodoSelectOption[] {
    const options = this.periodos.map((periodo) => ({
      id: periodo.id,
      descricao: String(periodo.descricao || 'Período'),
      dataInicial: periodo.dataInicial,
      dataFinal: periodo.dataFinal,
      encerrado: this.isPeriodoEncerrado(periodo)
    }));

    if (options.length > 0) {
      return options;
    }

    return this.colunas
      .filter((coluna) => coluna.tipoPeriodo === 'PERIODO_AVALIATIVO')
      .map((coluna) => {
        const rawValue = (coluna.rawValue || {}) as { id?: number | string; dataInicial?: string; dataFinal?: string };
        return {
        id: coluna.rawValue?.id ?? coluna.descricao,
        descricao: String(coluna.descricao || 'Período'),
        dataInicial: rawValue.dataInicial,
        dataFinal: rawValue.dataFinal,
        encerrado: false
      };
      });
  }

  get periodoSelecionadoId(): number | string | null {
    // ?? em vez de || pra não descartar id 0, que é falsy mas válido
    return this.periodo?.id ?? this.periodoOptions[0]?.id ?? null;
  }

  get notaReferenciaPublicacaoAnual(): NotaView | null {
    if (this.porInstrumento || this.isTurmaEjaModular) {
      return null;
    }

    const periodoId = this.periodoSelecionadoId;
    if (periodoId === null || periodoId === undefined) {
      return null;
    }

    const colunaIndex = this.colunas.findIndex((coluna) => String(coluna.rawValue?.id ?? coluna.descricao) === String(periodoId));
    if (colunaIndex < 0) {
      return null;
    }

    for (const linha of this.listaEnturmacoesOrdenada) {
      const nota = linha.notas?.[colunaIndex];
      if (nota) {
        return nota;
      }
    }

    return null;
  }

  togglePublicacaoAnualSelecionada(): void {
    this.abrirModalPublicacaoAnual();
  }

  abrirModalPublicacaoAnual(): void {
    this.publicacaoAnualModalAberta = true;
  }

  onPublicacaoAnualSuccess(event: {id: number, publicado: boolean, data: string | null}[]): void {
    event.forEach(resultado => {
      this.atualizarEstadoPublicacaoAvaliacao(resultado.id, resultado.publicado, resultado.data);
    });
  }

  triggerConfirmacaoDespublicar(item: {id: number, descricao: string}): void {
    this.avaliacaoConfirmacaoPublicar = false;
    this.avaliacaoConfirmacaoTitulo = `Desfazer publicação - ${item.descricao}`;
    this.avaliacaoPublicandoId = item.id;
    this.avaliacaoConfirmacaoAberta = true;
  }

  abrirModalDatasExames(): void {
    this.dataExamesModalAberta = true;
  }

  onDatasExamesSuccess(): void {
    this.notificationService.success({ 
      title: 'Sucesso', 
      message: 'Datas de exames e conselho atualizadas.' 
    });
  }

  get instrumentosRecuperacaoOptions(): InstrumentoResumoView[] {
    return this.instrumentoContextoResumo.filter((item) => item.tipoInstrumento === 'INSTRUMENTO_AVALIACAO' && item.id !== this.instrumentoForm.id);
  }

  get instrumentoRecuperacaoSelectOptions(): PrototypeSelectOption[] {
    return this.instrumentosRecuperacaoOptions.map((instrumento) => ({
      value: instrumento.id,
      label: `${instrumento.sigla} - ${instrumento.descricao}`
    }));
  }

  get shouldShowInstrumentoPeriodoField(): boolean {
    return !this.instrumentoTurmaEAtividade;
  }

  get instrumentoFormularioValido(): boolean {
    if (!this.instrumentoForm.turmaId) return false;
    if (!this.instrumentoForm.itemEducacionalId) return false;
    if (this.shouldShowInstrumentoPeriodoField && !this.instrumentoForm.periodoAvaliativoId) return false;
    if (!this.instrumentoForm.data) return false;
    if (!this.instrumentoForm.tipoInstrumento) return false;
    if (!this.instrumentoForm.descricao?.trim()) return false;
    const sigla = this.instrumentoForm.sigla?.trim();
    if (!sigla || sigla.length > 5) return false;
    if (this.instrumentoForm.tipoInstrumento === 'RECUPERACAO_PARALELA' && !this.instrumentoForm.instrumentoRecuperacaoId) return false;
    if (this.instrumentoTurmaEjaModular) return false;
    if (!this.instrumentoCamposAdicionaisObrigatoriosValidos) return false;
    return true;
  }

  onInstrumentoCamposAdicionaisValidChange(valid: boolean): void {
    this.instrumentoCamposAdicionaisObrigatoriosValidos = valid;
  }

  get instrumentoTurmaBloqueadaContexto(): boolean {
    return false;
  }

  get instrumentoExibeFaixaNotas(): boolean {
    return this.getModoAvaliacaoInstrumentoAtual() === 'NUMERICO';
  }

  get instrumentoExibePeso(): boolean {
    const modoAvaliacao = this.getModoAvaliacaoInstrumentoAtual();
    return modoAvaliacao === 'NUMERICO' || modoAvaliacao === 'CONCEITO_NUMERICO';
  }

  get instrumentoExibeCamposNumericos(): boolean {
    return this.instrumentoExibeFaixaNotas;
  }

  get instrumentoFormularioTitulo(): string {
    return this.instrumentoForm.id ? 'Editando instrumento de avaliação' : 'Adicionando instrumento de avaliação';
  }

  get instrumentoDataSelecionada(): Date | null {
    if (!this.instrumentoForm.data) return null;
    const [ano, mes, dia] = this.instrumentoForm.data.split('-').map(Number);
    return new Date(ano, mes - 1, dia, 12, 0, 0);
  }

  onInstrumentoDataChange(data: Date | null): void {
    if (!data) {
      this.instrumentoForm.data = '';
      return;
    }
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    this.instrumentoForm.data = `${ano}-${mes}-${dia}`;
  }

  get instrumentoErrosFormulario(): string[] {
    return this.validarFormularioInstrumento();
  }

  get instrumentoRecuperacaoVinculadaErros(): string[] {
    return this.validarRecuperacaoVinculada();
  }

  get instrumentoEdicaoSomenteDataPermitida(): boolean {
    return !!(this.instrumentoForm.id && this.instrumentoPeriodoEncerradoSelecionado);
  }

  get instrumentoFoiAlterado(): boolean {
    return this.serializeInstrumentoForm() !== this.instrumentoSnapshotInicial ||
      this.serializeInstrumentoArquivos() !== this.instrumentoArquivosSnapshotInicial ||
      this.serializeInstrumentoCampoAdicional() !== this.instrumentoCampoAdicionalSnapshotInicial;
  }

  get instrumentoDescricaoComponente(): string {
    return this.instrumentoItemSelecionado?.descricao || `Selecione ${this.labelItensEducacionais}`;
  }

  get instrumentoLabelItemEducacional(): string {
    return this.instrumentoTurmaEAtividade ? 'Atividade' : 'Componente curricular';
  }

  get instrumentoDescricaoPeriodo(): string {
    return this.instrumentoPeriodoSelecionado ? this.formatPeriodoDescricao(this.instrumentoPeriodoSelecionado) : 'Sem período avaliativo';
  }

  protected get isStandaloneInstrumentosPage(): boolean {
    return false;
  }

  get instrumentoAnexosItems(): AnexoItem[] {
    return this.instrumentoArquivos.map((arquivo, index) => ({
      id: this.resolveInstrumentoArquivoIdentificador(arquivo, index),
      nome: arquivo.nome,
      tamanho: arquivo.tamanho
    }));
  }

  get instrumentoAnexosLabel(): string {
    return buildInstrumentoAnexosLabel(INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS);
  }

  get instrumentoAnexosTamanhoMaximoMb(): number {
    return Math.round(INSTRUMENTO_ANEXO_TAMANHO_MAXIMO / (1024 * 1024));
  }

  get instrumentoNotaMinimaLimite(): number {
    const notaMinimaConfig = Number((this.instrumentoConfiguracaoTurma as { notaMinima?: number } | null)?.notaMinima);
    return !Number.isNaN(notaMinimaConfig) ? notaMinimaConfig : 0;
  }

  get instrumentoNotaMaximaLimite(): number {
    const notaMaximaConfig = Number((this.instrumentoConfiguracaoTurma as { notaMaxima?: number } | null)?.notaMaxima);
    return !Number.isNaN(notaMaximaConfig) ? notaMaximaConfig : 100;
  }

  get instrumentoPesoMinimoLimite(): number {
    return 1;
  }

  get instrumentoPesoMaximoLimite(): number {
    return 100;
  }

  get instrumentoNotaMinimaFormatada(): string {
    const valor = this.instrumentoForm.notaMinima;
    return valor ? String(valor).replace('.', ',') : '';
  }

  get instrumentoNotaMaximaFormatada(): string {
    const valor = this.instrumentoForm.notaMaxima;
    return valor ? String(valor).replace('.', ',') : '';
  }

  get instrumentoPesoFormatado(): string {
    const valor = this.instrumentoForm.peso;
    return valor ? String(valor).replace('.', ',') : '';
  }

  protected getTipoInstrumentoLabel(tipo: TipoInstrumentoAvaliacaoId | string | null | undefined): string {
    return getTipoInstrumentoLabel(tipo);
  }

  orderBy(item: LinhaDesempenho): number {
    return item.enturmacao.numeroChamada;
  }

  verificaDataFimEnturmacao(nota: NotaView): boolean {
    return verificaDataFimEnturmacao(nota);
  }

  showParecer(condicao?: boolean): boolean {
    return deveExibirParecer(condicao, this.configuracaoAvaliacao);
  }

  onShowParecer(nota: NotaView | unknown): void {
    if (!nota || typeof nota !== 'object') return;
    const notaView = nota as NotaViewCompat;
    this.parecerModalNota = notaView;
    this.parecerModalTexto = String(notaView.parecerDetalhadoConteudo || notaView.parecer || '');
    this.parecerModalEhEdicao = !!(notaView.parecerDetalhadoConteudo || notaView.parecer);
    this.parecerModalAberto = true;

    if (!notaView.id) {
      return;
    }

    this.parecerModalCarregando = true;
    this.parecerService.getParecerDetalhado(notaView.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (parecer: Parecer) => {
          this.parecerModalTexto = String(parecer?.parecer || this.parecerModalTexto || '');
          this.parecerModalCarregando = false;
        },
        error: () => {
          this.parecerModalCarregando = false;
        }
      });
  }

  onShowAvaliacaoDescritiva(nota: NotaView | unknown): void {
    if (!nota || typeof nota !== 'object') {
      return;
    }

    const notaView = nota as NotaView;
    notaView.$$modoAvaliacao = 'DESCRITIVO';
    this.avaliacaoDescritivaModalNota = notaView;
    this.avaliacaoDescritivaModalTexto = String(notaView.$$nota || notaView.notaDescritiva || '');
    this.avaliacaoDescritivaModalAberto = true;
  }

  onShowCHA(nota: NotaView | unknown): void {
    if (!nota || typeof nota !== 'object') return;
    // Implementar abertura de modal de CHA
  }

  toggleOcultarDetalhes(): void {
    this.ocultarDetalhes = !this.ocultarDetalhes;
  }

  podeExcluirInstrumento(instrumento: InstrumentoResumoView): boolean {
    if (this.isExclusaoInstrumentoBloqueada(instrumento)) {
      if (this.isInstrumentoBloqueadoPorEncerramento(instrumento)) {
        this.notificationService.warning({
          title: 'Período encerrado',
          message: 'Não é permitido excluir instrumentos de períodos avaliativos encerrados.'
        });
      } else {
        this.notificationService.warning({
          title: 'Exclusão bloqueada',
          message: 'Não é permitido excluir instrumentos que possuem recuperação paralela vinculada.'
        });
      }
      return false;
    }

    return true;
  }

  podeAlternarPublicacaoInstrumento(instrumento: InstrumentoResumoView): boolean {
    if (this.isPublicacaoInstrumentoBloqueada(instrumento)) {
      this.notificationService.warning({
        title: 'Publicação bloqueada',
        message: 'Não é permitido publicar ou desfazer a publicação dos desempenhos dos alunos quando o período avaliativo ou o período letivo estiver encerrado.'
      });
      return false;
    }

    return true;
  }

  isExclusaoInstrumentoBloqueada(instrumento: InstrumentoResumoView): boolean {
    return this.isInstrumentoBloqueadoPorEncerramento(instrumento) ||
      !!this.getRecuperacaoParalelaVinculada(instrumento.id);
  }

  isPublicacaoInstrumentoBloqueada(instrumento: InstrumentoResumoView): boolean {
    return this.isInstrumentoBloqueadoPorEncerramento(instrumento) ||
      (!instrumento.periodoAvaliativoId && this.isPeriodoLetivoEncerrado(this.instrumentoEncerramentosTurma));
  }

  formatarDataHoraPublicacao(dataPublicacao: string): string {
    return formatarDataHoraPublicacao(dataPublicacao);
  }

  private isInstrumentoBloqueadoPorEncerramento(instrumento: InstrumentoResumoView): boolean {
    if (!instrumento.periodoAvaliativoId || !this.turma?.id) {
      return false;
    }

    const periodo = this.periodos.find((item) => item.id === instrumento.periodoAvaliativoId) || null;
    return periodo ? this.isPeriodoEncerrado(periodo) : false;
  }

  getSituacao(linha: LinhaDesempenho): string | null {
    let situacaoRaw = linha.enturmacao.situacao || linha.enturmacao.matricula?.situacao || '';

    const situacoesItens = (linha.enturmacao as any).situacaoItensEducacionais;
    if (this.itemEducacional && Array.isArray(situacoesItens)) {
      const sitEspecifica = situacoesItens.find((s: any) => s.itemEducacional?.id === this.itemEducacional?.id);
      if (sitEspecifica && sitEspecifica.situacao) {
        situacaoRaw = sitEspecifica.situacao;
      }
    }

    const situacaoBase = String(situacaoRaw).toUpperCase();

    const situacao = this.enumSituacaoDisciplina[situacaoBase]?.description ||
                    this.enumSituacaoMatricula[situacaoBase]?.description ||
                    getSituacaoDescricaoCurta(situacaoBase);

    const mostraSituacao = this.turma?.tipoResultado !== 'AREA_CONHECIMENTO';
    return mostraSituacao ? (situacao || null) : null;
  }

  trackByEnturmacao(index: number, linha: LinhaDesempenho): number | string {
    return linha.enturmacao?.id || index;
  }

  trackByItemEducacional(index: number, item: ItemEducacional): number {
    return item.id ?? index;
  }

  trackByInstrumentoResumo(index: number, instrumento: InstrumentoResumoView): number {
    return instrumento.id || index;
  }
}